import { sidebarPage } from '../utils/sidebarPageLayout.js';
import { FundAdmin } from './FundAdmin.js';
import { t, resolveFundLang, getStoredFundLang } from '../fund/fundI18n.js';
import {
    loadAllCampaigns,
    persistCampaign,
    upsertDraft,
    deleteDraftById,
    loadDraftsForUser,
    uniqueCampaignId,
    getCampaignShareUrl,
    isCampaignClosed,
    normalizeProject,
    slugify,
} from '../fund/fundStore.js';
import {
    infoIcon,
    labelWithHelp,
    attachInfoPopovers,
    showToast,
    renderLangBar,
    attachLangBar,
    renderFeeVisualizer,
    renderGallery,
    attachGallery,
    renderFaqAccordion,
    renderCountdown,
} from '../fund/fundUi.js';
import { getCurrentUser } from '../data/auth.js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../src/firebase.js';
import {
    loadCreatorStripeProfile,
    refreshStripeConnectStatus,
    startStripeConnectOnboarding,
    isCampaignFinanciallyLocked,
} from '../fund/fundStripe.js';
import { renderRichEditorHtml, attachRichEditor } from '../fund/fundRichEditor.js';
import {
    buildUtmLinks,
    recordCampaignView,
    getCampaignViews,
    getLocalClicks as getLocalClicksFromModule,
    loadCampaignClicks,
    aggregateClicksBySource,
    aggregateDonationsByDay,
    getBackerFeed,
    getTopUpTotals,
    renderBarChart,
} from '../fund/fundAnalytics.js';
import { addCampaignFollower, getCampaignFollowers, mergeFollowerLists } from '../fund/fundComingSoon.js';
import {
    createUpdatePayload,
    getBackerEmailsFromProject,
    renderUpdatesListHtml,
} from '../fund/fundUpdates.js';
import {
    getCollaborators,
    getCollaboratorsLocal,
    inviteCollaborator,
    revokeCollaborator,
    canManageCampaign,
    canViewAnalytics,
    acceptInvite,
} from '../fund/fundTeam.js';
import {
    generatePreviewTokenFields,
    isPreviewTokenValid,
    buildPreviewShareUrl,
    previewExpiresLabel,
} from '../fund/fundPreview.js';
import { createPollFromForm, recordLocalVote, hasVoted, renderPollResultsDashboard } from '../fund/fundPolls.js';
import {
    buildSecretTierUrl,
    generateSecretToken,
    getPublicRewardTiers,
    findTierBySecretToken,
} from '../fund/fundSecretTiers.js';
import {
    renderCommunityCommentsHtml,
    createCommentPayload,
} from '../fund/fundComments.js';
import {
    renderTierDeliveryHtml,
    monthYearOptions,
    getBackersForTier,
} from '../fund/fundTiers.js';
import {
    loadKycStatus,
    startStripeIdentityVerification,
    refreshKycStatus,
} from '../fund/fundKyc.js';

export class Fund {
    constructor(containerId) {
        this.containerId = containerId;
        this.currentView = 'browse'; // browse | detail | create | confirmation | donate | donate-success | admin | analytics | coming-soon | drafts
        this.selectedProjectId = null;
        this.newProjectId = null;
        this.currentCategory = 'All';
        this.projects = [];
        this.categories = ['All', 'Clubs', 'Players', 'Events', 'Facilities', 'Coaching', 'Media'];
        
        // Create wizard (Steps 1–4)
        this.formStep = 1;
        this.newProject = {
            title: '',
            category: 'Clubs',
            shortDesc: '',
            fullDesc: '',
            target: 2000,
            endDate: '',
            rewardTiers: [],
            imageURL: '',
            videoURL: '',
            creatorName: '',
            email: ''
        };

        // Donation State (for View 4)
        this.donationAmount = 25;
        this.donorName = '';
        this.donorEmail = '';
        this.donorMessage = '';
        this.selectedRewardTierId = '';
        this.stripeInstance = null;
        this.stripeElements = null;
        this.stripeCard = null;
        this.paymentIsProcessing = false;
        this.fundAdmin = new FundAdmin(this);

        this.profileLang = getStoredFundLang();
        Object.assign(this, resolveFundLang(this.profileLang));
        this.searchQuery = '';
        this.draftId = null;
        this.lastSavedAt = null;
        this.previewMode = false;
        this.donationTopUp = 0;
        this.hideDonorName = false;
        this.autosaveTimer = null;
        this.newProject.faqs = [];
        this.newProject.images = [];
        this.newProject.bodyHtml = '';
        this.newProject.comingSoonEnabled = false;
        this.newProject.status = 'draft';
        this._getRichEditorHtml = null;
        this._loading = true;
        this.creatorStripe = {
            stripeAccountId: null,
            stripeVerified: false,
        };
        this.creatorKyc = { kycVerified: false };
        this.filterVerifiedOnly = false;
        this.detailTab = 'story';
        this._reviewShareUrl = '';
        this.secretTierToken = '';

        this.initData();
    }

    async refreshCreatorStripe() {
        const user = getCurrentUser();
        if (!user) {
            this.creatorStripe = { stripeAccountId: null, stripeVerified: false };
            this.creatorKyc = { kycVerified: false };
            return;
        }
        this.creatorStripe = await loadCreatorStripeProfile(user.uid);
        this.creatorKyc = await loadKycStatus(user.uid);
    }

    isCampaignVerified(p) {
        return !!(p?.kycVerified || p?.stripeVerified);
    }

    renderConnectPanel() {
        const user = getCurrentUser();
        if (!user) {
            return `<div class="fg-fund-connect fg-fund-connect--warn">
                <p>${this.tr('connectSignIn')}</p>
            </div>`;
        }
        const v = this.creatorStripe?.stripeVerified;
        const statusClass = v ? 'fg-fund-connect--ok' : 'fg-fund-connect--pending';
        const statusText = v ? this.tr('connectVerified') : this.tr('connectPending');
        const btnLabel = this.creatorStripe?.stripeAccountId ? this.tr('connectResume') : this.tr('connectBtn');
        return `
            <div class="fg-fund-connect ${statusClass}">
                <div class="fg-fund-connect__head">
                    <h4>${this.tr('connectTitle')} ${infoIcon('connect', this)}</h4>
                    <span class="fg-fund-connect__badge">${statusText}</span>
                </div>
                <p class="fg-fund-connect__sub">${this.tr('connectSub')}</p>
                <button type="button" class="fg-fund-btn fg-fund-btn--primary" id="stripe-connect-btn">${btnLabel}</button>
            </div>`;
    }

    canPublishCampaign() {
        const user = getCurrentUser();
        if (!user) return false;
        return !!this.creatorStripe?.stripeVerified;
    }

    tr(key) {
        return t(this.locale, key);
    }

    async loadUserLanguage() {
        const user = getCurrentUser();
        if (!user) return;
        try {
            const snap = await getDoc(doc(db, 'users', user.uid));
            const pref = snap.data()?.preferredLanguage;
            if (pref) {
                this.profileLang = pref;
                Object.assign(this, resolveFundLang(pref));
            }
        } catch (_) { /* use stored */ }
    }

    getFundMeta() {
        try {
            return JSON.parse(localStorage.getItem('fg_fund_meta') || '{}');
        } catch {
            return {};
        }
    }

    saveFundMeta(meta) {
        localStorage.setItem('fg_fund_meta', JSON.stringify(meta));
    }

    toggleFeaturedProject(projectId) {
        const meta = this.getFundMeta();
        const ids = new Set(meta.featuredIds || []);
        if (ids.has(projectId)) ids.delete(projectId);
        else ids.add(projectId);
        meta.featuredIds = [...ids];
        this.saveFundMeta(meta);
    }

    removeProject(projectId) {
        this.projects = this.projects.filter((p) => p.id !== projectId);
        localStorage.setItem('fg_fund_projects', JSON.stringify(this.projects));
        const meta = this.getFundMeta();
        meta.featuredIds = (meta.featuredIds || []).filter((id) => id !== projectId);
        this.saveFundMeta(meta);
    }

    getBrowseProjects() {
        const meta = this.getFundMeta();
        const featured = new Set(meta.featuredIds || []);
        const q = (this.searchQuery || '').toLowerCase().trim();
        const filtered = this.projects.filter((p) => {
            if (p.status === 'draft' || p.status === 'coming_soon') return false;
            if (this.currentCategory !== 'All' && p.category.toLowerCase() !== this.currentCategory.toLowerCase()) return false;
            if (q && !`${p.title} ${p.creator} ${p.description}`.toLowerCase().includes(q)) return false;
            return true;
        });
        return filtered.sort((a, b) => {
            const af = featured.has(a.id) ? 0 : 1;
            const bf = featured.has(b.id) ? 0 : 1;
            if (af !== bf) return af - bf;
            return (b.raised || 0) - (a.raised || 0);
        });
    }

    isAdminUser() {
        return this.fundAdmin.isAdmin();
    }

    initData() {
        const stored = localStorage.getItem('fg_fund_projects');
        if (stored) {
            let renamedCorkSample = false;
            this.projects = JSON.parse(stored).map((p) => {
                if (p.id !== 'cork-facilities' && p.creator !== 'Cork FC Women') return p;
                renamedCorkSample = true;
                return {
                    ...p,
                    id: p.id === 'cork-facilities' ? 'harbour-united-facilities' : p.id,
                    title: (p.title || '').replace(/Cork FC Women/gi, 'Harbour United WFC').replace(/Cork FC/gi, 'Harbour United'),
                    creator: 'Harbour United WFC',
                    rewardTiers: (p.rewardTiers || []).map((t) => ({
                        ...t,
                        id: String(t.id || '').replace(/^tier-cork-/, 'tier-harbour-'),
                        description: (t.description || '').replace(/Cork FC scarf/gi, 'club scarf'),
                    })),
                };
            });
            if (renamedCorkSample) {
                localStorage.setItem('fg_fund_projects', JSON.stringify(this.projects));
            }
        } else {
            const defaultProjects = [
                {
                    id: 'harbour-united-facilities',
                    title: "New changing facilities for Harbour United WFC",
                    category: "Facilities",
                    target: 15000,
                    raised: 9400,
                    endDate: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString(), // 18 Days Left
                    description: "Help us build proper changing rooms for our 80 female players who currently use a converted storage room.",
                    creator: "Harbour United WFC",
                    imageGradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                    imageURL: "https://images.unsplash.com/photo-1761234201591-e3185fff2348?w=600&q=80",
                    donorsCount: 42,
                    updates: [
                        { date: "May 20, 2026", text: "Architect plans have been finalized! Thank you for the support." }
                    ],
                    comments: [
                        { donorName: "Laura G.", message: "This is so important for the girls! Backing this project.", amount: 50, timestamp: "May 28, 2026" }
                    ],
                    rewardTiers: [
                        { id: 'tier-harbour-1', name: "Bronze Supporter", minDonation: 10, description: "Get a thank-you shoutout on our social channels." },
                        { id: 'tier-harbour-2', name: "Silver Supporter", minDonation: 50, description: "Receive a commemorative digital certificate and club scarf." },
                        { id: 'tier-harbour-3', name: "Gold Supporter", minDonation: 250, description: "Your name will be engraved on our new donor plaque in the facilities." }
                    ]
                },
                {
                    id: 'lisbon-players',
                    title: "Send Lisbon U-17 Girls to Tournament in Paris",
                    category: "Players",
                    target: 4500,
                    raised: 1200,
                    endDate: new Date(Date.now() + 31 * 24 * 60 * 60 * 1000).toISOString(), // 31 Days Left
                    description: "Travel and accommodation costs for 18 players to compete in the Paris International U-17 Cup.",
                    creator: "Lisbon Youth Football",
                    imageGradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    imageURL: "https://images.unsplash.com/photo-1535506197904-e5c09c0e5619?w=600&q=80",
                    donorsCount: 12,
                    updates: [],
                    comments: [],
                    rewardTiers: [
                        { id: 'tier-lisbon-1', name: "Backer", minDonation: 15, description: "Get a digital postcard from Paris signed by the team." }
                    ]
                },
                {
                    id: 'podcast-media',
                    title: "Launch Women's Football Podcast — Season 2",
                    category: "Media",
                    target: 3000,
                    raised: 3000,
                    endDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // Ended (0 Days Left)
                    description: "Fully funded! Production costs for 12-episode season covering Liga F and WSL.",
                    creator: "FootballGirl Podcast Network",
                    imageGradient: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
                    imageURL: "https://images.unsplash.com/photo-1709846486154-f9172678be6f?w=600&q=80",
                    donorsCount: 60,
                    updates: [
                        { date: "May 29, 2026", text: "We reached 100% of our target! Recording starts next week." }
                    ],
                    comments: [
                        { donorName: "Elena R.", message: "Can't wait for Season 2! Keep up the amazing work.", amount: 25, timestamp: "May 30, 2026" }
                    ],
                    rewardTiers: [
                        { id: 'tier-podcast-1', name: "Podcast Fan", minDonation: 5, description: "Access exclusive ad-free episodes." }
                    ]
                },
                {
                    id: 'lagos-coaching',
                    title: "Coaching Badges for 10 Female Coaches in Lagos",
                    category: "Coaching",
                    target: 5000,
                    raised: 2750,
                    endDate: new Date(Date.now() + 44 * 24 * 60 * 60 * 1000).toISOString(), // 44 Days Left
                    description: "UEFA C licence funding for 10 women coaches who cannot afford the course fees.",
                    creator: "Lagos Women's Academy",
                    imageGradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    imageURL: "",
                    donorsCount: 24,
                    updates: [],
                    comments: [],
                    rewardTiers: [
                        { id: 'tier-lagos-1', name: "Coach Supporter", minDonation: 25, description: "Get invited to a live Zoom Q&A session with the coaches." }
                    ]
                },
                {
                    id: 'tbilisi-clubs',
                    title: "New kit for Tbilisi Women FC — first season in top flight",
                    category: "Clubs",
                    target: 2500,
                    raised: 890,
                    endDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(), // 12 Days Left
                    description: "Help kit out a historic Georgian club competing in their first ever top-flight season.",
                    creator: "Tbilisi Women FC",
                    imageGradient: "linear-gradient(135deg, #7028e4 0%, #e5e5be 100%)",
                    imageURL: "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=600&q=80",
                    donorsCount: 9,
                    updates: [],
                    comments: [],
                    rewardTiers: [
                        { id: 'tier-tbilisi-1', name: "Kit Backer", minDonation: 20, description: "Get a digital team photo showing off the new kit." }
                    ]
                },
                {
                    id: 'kenya-events',
                    title: "Women's Football Skills Camp — Rural Kenya",
                    category: "Events",
                    target: 6000,
                    raised: 4100,
                    endDate: new Date(Date.now() + 22 * 24 * 60 * 60 * 1000).toISOString(), // 22 Days Left
                    description: "Week-long skills and education camp for 200 girls aged 10-16 in the Rift Valley region.",
                    creator: "Kenya Sports Outreach",
                    imageGradient: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
                    imageURL: "https://images.unsplash.com/photo-1778016740636-5ef0aaab1e97?w=600&q=80",
                    donorsCount: 33,
                    updates: [],
                    comments: [],
                    rewardTiers: [
                        { id: 'tier-kenya-1', name: "Camp Supporter", minDonation: 30, description: "Receive a personalized video message from the camp attendees." }
                    ]
                }
            ];
            this.projects = defaultProjects;
            localStorage.setItem('fg_fund_projects', JSON.stringify(defaultProjects));
        }
    }

    async init(nav = {}) {
        const el = document.getElementById(this.containerId);
        if (!el) {
            console.error('[FundPage]: Target container not found');
            return;
        }

        this.injectStyles();
        await this.loadUserLanguage();
        await this.refreshCreatorStripe();
        this.projects = (await loadAllCampaigns()).map(normalizeProject);
        this._loading = false;

        const hashParams = Object.fromEntries(
            new URLSearchParams((window.location.hash || '').slice(1)).entries()
        );
        const projectId = nav.projectId || nav.project || hashParams.project;
        const tierToken = nav.tier || hashParams.tier || '';
        if (tierToken) this.secretTierToken = tierToken;
        if (hashParams.connect === 'return' || hashParams.connect === 'refresh') {
            await refreshStripeConnectStatus();
            await this.refreshCreatorStripe();
            this.currentView = 'create';
            this.formStep = 4;
            showToast(this.creatorStripe.stripeVerified
                ? this.tr('connectVerified')
                : this.tr('connectPending'));
        }
        if (hashParams.kyc === 'return') {
            const user = getCurrentUser();
            if (user) {
                const status = await refreshKycStatus(user.uid);
                await this.refreshCreatorStripe();
                if (status.kycVerified) {
                    for (const p of this.projects) {
                        if (p.ownerUid === user.uid && !p.kycVerified) {
                            p.kycVerified = true;
                            await persistCampaign(p);
                        }
                    }
                }
                showToast(status.kycVerified ? this.tr('kycVerified') : this.tr('kycPending'));
            }
            this.currentView = 'analytics';
            if (projectId) this.selectedProjectId = projectId;
        }

        if (nav.view === 'drafts') {
            this.currentView = 'drafts';
        } else if (hashParams.invite && projectId) {
            const user = getCurrentUser();
            if (user && await acceptInvite(projectId, hashParams.invite, user.uid)) {
                showToast(this.tr('inviteAccepted'));
            }
            this.selectedProjectId = projectId;
            this.currentView = 'analytics';
        } else if ((nav.view === 'preview' || hashParams.view === 'preview') && projectId) {
            const token = nav.token || hashParams.token;
            let project = this.projects.find((p) => p.id === projectId);
            if (!project) {
                const draft = loadDraftsForUser().find((d) => d.draftId === projectId);
                if (draft) {
                    project = normalizeProject({ id: projectId, ...draft, creator: draft.creatorName || 'Creator' });
                    this.projects.push(project);
                }
            }
            if (project && isPreviewTokenValid(project, token)) {
                this.selectedProjectId = projectId;
                this.previewMode = true;
                this.currentView = 'detail';
            } else {
                showToast(this.tr('previewInvalid'), 'error');
                this.currentView = 'browse';
            }
        } else if (nav.view === 'coming-soon' && projectId) {
            this.selectedProjectId = projectId;
            this.currentView = 'coming-soon';
        } else if (nav.view === 'analytics' && projectId) {
            this.selectedProjectId = projectId;
            this.currentView = 'analytics';
        } else if (projectId) {
            this.selectedProjectId = projectId;
            this.currentView = 'detail';
            const proj = this.projects.find((p) => p.id === projectId);
            const secretTier = proj && findTierBySecretToken(proj, tierToken);
            if (secretTier) {
                this.selectedRewardTierId = secretTier.id;
                showToast(this.tr('privateTierUnlocked'));
            }
        }

        this.render();
        this.startAutosave();
    }

    destroy() {
        if (this.autosaveTimer) clearInterval(this.autosaveTimer);
    }

    startAutosave() {
        if (this.autosaveTimer) clearInterval(this.autosaveTimer);
        this.autosaveTimer = setInterval(() => {
            if (this.currentView === 'create') this.saveDraftSilent();
        }, 15000);
    }

    collectFormState() {
        const root = document.getElementById(this.containerId);
        if (!root) return { ...this.newProject };
        return {
            ...this.newProject,
            title: root.querySelector('#p-title')?.value?.trim() || this.newProject.title,
            category: root.querySelector('#p-category')?.value || this.newProject.category,
            shortDesc: root.querySelector('#p-short-desc')?.value?.trim() || this.newProject.shortDesc,
            fullDesc: root.querySelector('#p-full-desc')?.value?.trim() || this.newProject.fullDesc,
            target: Number(root.querySelector('#p-target')?.value) || this.newProject.target,
            endDate: root.querySelector('#p-end-date')?.value || this.newProject.endDate,
            imageURL: root.querySelector('#p-image')?.value?.trim() || this.newProject.imageURL,
            videoURL: root.querySelector('#p-video')?.value?.trim() || this.newProject.videoURL,
            creatorName: root.querySelector('#p-creator')?.value?.trim() || this.newProject.creatorName,
            email: root.querySelector('#p-email')?.value?.trim() || this.newProject.email,
            images: this.parseGalleryInput(root.querySelector('#p-gallery')?.value),
        };
    }

    parseGalleryInput(raw) {
        if (!raw) return this.newProject.images || [];
        return raw.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 8);
    }

    async saveDraftSilent() {
        const state = this.collectFormState();
        if (!state.title && !state.shortDesc) return;
        const entry = await upsertDraft({
            ...state,
            draftId: this.draftId,
            ownerUid: getCurrentUser()?.uid,
        });
        this.draftId = entry.draftId;
        this.lastSavedAt = Date.now();
        const stamp = document.getElementById('fg-fund-saved-stamp');
        if (stamp) stamp.textContent = `${this.tr('saved')} ${new Date().toLocaleTimeString()}`;
    }

    render() {
        const container = document.getElementById(this.containerId);
        if (!container) return;

        let contentHtml = '';

        switch (this.currentView) {
            case 'browse':
                contentHtml = this.renderBrowse();
                break;
            case 'detail':
                contentHtml = this.renderDetail();
                break;
            case 'create':
                contentHtml = this.renderCreate();
                break;
            case 'confirmation':
                contentHtml = this.renderConfirmation();
                break;
            case 'donate':
                contentHtml = this.renderDonate();
                break;
            case 'donate-success':
                contentHtml = this.renderDonateSuccess();
                break;
            case 'admin':
                contentHtml = this.fundAdmin.render();
                break;
            case 'drafts':
                contentHtml = this.renderDraftsHub();
                break;
            case 'analytics':
                contentHtml = this.renderAnalytics();
                break;
            case 'coming-soon':
                contentHtml = this.renderComingSoon();
                break;
            default:
                contentHtml = this.renderBrowse();
        }

        if (this._loading) {
            contentHtml = `<div class="fg-fund-loading">${this.tr('fundTitle')}…</div>`;
        }

        container.innerHTML = sidebarPage({
            title: 'football<span class="fg-brand-girl">girl</span> fund',
            subtitle: this.tr('fundSubtitle'),
            main: `${renderLangBar(this)}${contentHtml}`,
            wide: true,
        });
        container.dir = this.rtl ? 'rtl' : 'ltr';

        this.attachEvents();
        attachInfoPopovers(container, this);
        attachLangBar(container, this, () => this.render());
        attachGallery(container);
    }

    // ==========================================
    // VIEW 1: BROWSE VIEW
    // ==========================================
    renderBrowse() {
        const meta = this.getFundMeta();
        const filteredProjects = this.getBrowseProjects();
        const adminBadge = this.isAdminUser()
            ? `<button type="button" class="fg-fund-admin__hero-badge" id="fund-admin-open">Admin</button>`
            : '';
        const heroBanner = meta.heroBanner
            ? `<p class="fg-fund-hero__banner">${meta.heroBanner}</p>`
            : '';

        // Category Tabs
        const categoryTabsHtml = this.categories.map(cat => {
            const isActive = cat.toLowerCase() === this.currentCategory.toLowerCase();
            return `<button class="fg-fund-filter-chip ${isActive ? 'active' : ''}" data-category="${cat}">${cat}</button>`;
        }).join('');

        // Projects Grid
        const gridHtml = filteredProjects.length > 0 ? filteredProjects.map(p => {
            const pct = Math.min(Math.round((p.raised / p.target) * 100), 100);
            
            // Calculate days left
            const daysLeft = Math.max(0, Math.ceil((new Date(p.endDate) - new Date()) / (1000 * 60 * 60 * 24)));
            
            const isFeatured = (meta.featuredIds || []).includes(p.id);
            if (this.filterVerifiedOnly && !this.isCampaignVerified(p)) return '';
            const verifiedBadge = [
                p.kycVerified ? `<span class="fg-fund-kyc-badge">${this.tr('kycVerified')}</span>` : '',
                p.stripeVerified ? `<span class="fg-fund-verified-badge">${this.tr('verifiedCreator')}</span>` : '',
            ].join('');
            return `
                <article class="fg-fund-card ${isFeatured ? 'fg-fund-card--featured' : ''}" data-id="${p.id}">
                    <div class="fg-fund-card__image" style="background-image: ${p.imageURL ? `url('${p.imageURL}')` : p.imageGradient};"></div>
                    <div class="fg-fund-card__body">
                        <span class="fg-fund-card__category">${p.category}</span>
                        ${verifiedBadge}
                        <h3 class="fg-fund-card__title">${p.title}</h3>
                        <p class="fg-fund-card__creator">By <strong>${p.creator}</strong></p>
                        <p class="fg-fund-card__desc">${p.description}</p>
                        
                        <div class="fg-fund-card__progress">
                            <div class="fg-fund-card__progress-bar">
                                <div class="fg-fund-card__progress-fill" style="width: ${pct}%"></div>
                            </div>
                            <div class="fg-fund-card__progress-info">
                                <span class="fg-fund-card__percentage">${pct}% funded</span>
                                <span class="fg-fund-card__raised"><strong>€${p.raised.toLocaleString()}</strong> raised</span>
                            </div>
                        </div>
                        
                        <div class="fg-fund-card__footer">
                            <div class="fg-fund-card__days">
                                <span class="fg-fund-card__days-number">${daysLeft}</span>
                                <span class="fg-fund-card__days-label">days left</span>
                            </div>
                            <button class="fg-fund-btn fg-fund-btn--card-donate" data-id="${p.id}">Donate</button>
                        </div>
                    </div>
                </article>
            `;
        }).join('') : `
            <div class="fg-fund-empty">
                <h3>No projects found</h3>
                <p>Be the first to launch a campaign in the "${this.currentCategory}" category!</p>
            </div>
        `;

        return `
            <div class="fg-fund-browse animate-fade-in">
                <!-- Hero Section -->
                <section class="fg-fund-hero">
                    ${adminBadge}
                    <div class="fg-fund-hero__content">
                        <h1>Fund Women's Football</h1>
                        <p>Empower female players, build grassroots facilities, and support community initiatives directly.</p>
                        ${heroBanner}
                        <button class="fg-fund-btn fg-fund-btn--hero-start" id="start-project-btn">${this.tr('startProject')}</button>
                        <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="fund-drafts-btn">${this.tr('myDrafts')}</button>
                    </div>
                </section>

                <div class="fg-fund-search-row">
                    <input type="search" id="fund-search" class="fg-input fg-fund-search" placeholder="${this.tr('searchPlaceholder')}" value="${this.searchQuery}">
                    ${infoIcon('search', this)}
                </div>
                
                <div class="fg-fund-filters-container">
                    <div class="fg-fund-filters">
                        ${categoryTabsHtml}
                        <button type="button" class="fg-fund-filter-chip ${this.filterVerifiedOnly ? 'active' : ''}" id="filter-verified-only">${this.tr('verifiedOnly')}</button>
                    </div>
                </div>
                
                <!-- Project Grid -->
                <section class="fg-fund-grid">
                    ${gridHtml}
                </section>
            </div>
        `;
    }

    // ==========================================
    // VIEW 2: INDIVIDUAL CAMPAIGN VIEW
    // ==========================================
    renderDraftsHub() {
        const drafts = loadDraftsForUser();
        const rows = drafts.length
            ? drafts.map((d) => `
                <div class="fg-fund-draft-row">
                    <div>
                        <strong>${d.title || 'Untitled draft'}</strong>
                        <span class="fg-fund-draft-row__meta">${new Date(d.updatedAt).toLocaleString()}</span>
                    </div>
                    <div class="fg-fund-draft-row__actions">
                        <button type="button" class="fg-fund-btn fg-fund-btn--primary" data-draft-continue="${d.draftId}">${this.tr('continueEdit')}</button>
                        <button type="button" class="fg-fund-btn fg-fund-btn--danger-text" data-draft-delete="${d.draftId}">${this.tr('deleteDraft')}</button>
                    </div>
                </div>`).join('')
            : `<p class="fg-fund-empty-sub">No drafts yet — start a campaign to auto-save progress.</p>`;

        return `
            <div class="fg-fund-drafts animate-fade-in">
                <button class="fg-fund-back-link" id="go-back-browse-link">${this.tr('backBrowse')}</button>
                <h2>${this.tr('draftsTitle')}</h2>
                ${infoIcon('drafts', this)}
                <div class="fg-fund-draft-list">${rows}</div>
            </div>`;
    }

    renderDetail() {
        const project = this.projects.find(p => p.id === this.selectedProjectId);
        if (!project) return `<div class="fg-fund-error">Campaign not found. <button class="fg-fund-btn" id="go-back-browse">Back to Browse</button></div>`;

        const closed = isCampaignClosed(project);
        const pct = Math.min(Math.round((project.raised / project.target) * 100), 100);
        const daysLeft = Math.max(0, Math.ceil((new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24)));
        const donorsCount = project.donorsCount || 0;
        const images = project.images?.length ? project.images : (project.imageURL ? [project.imageURL] : []);
        const heroBg = images[0] ? `url('${images[0]}')` : project.imageGradient;
        const fullText = project.bodyHtml || project.fullStory || project.description;
        const previewBanner = this.previewMode
            ? `<div class="fg-fund-preview-banner">${this.tr('previewBanner')} — ${this.tr('noDonatePreview')}</div>`
            : '';
        const galleryHtml = images.length > 1 ? renderGallery(images, this) : '';
        const faqHtml = renderFaqAccordion(project.faqs, this);
        const countdownHtml = renderCountdown(project.endDate, this);
        const isOwner = getCurrentUser()?.uid && project.ownerUid === getCurrentUser().uid;

        const visibleTiers = getPublicRewardTiers(project, this.secretTierToken);
        const privateBanner = (project.rewardTiers || []).some((t) => t.isPrivate && findTierBySecretToken(project, this.secretTierToken))
            ? `<p class="fg-fund-secret-tier-banner">${this.tr('privateTierUnlocked')}</p>`
            : '';

        // Reward tiers
        const rewardTiersHtml = visibleTiers.map(tier => `
            <div class="fg-fund-reward-card" data-tier-id="${tier.id}">
                <div class="fg-fund-reward-card__header">
                    <span class="fg-fund-reward-card__min">Pledge €${tier.minDonation} or more</span>
                    <h4 class="fg-fund-reward-card__name">${tier.name}</h4>
                </div>
                <p class="fg-fund-reward-card__desc">${tier.description}</p>
                ${renderTierDeliveryHtml(tier, this)}
                <button class="fg-fund-btn fg-fund-btn--reward" data-amount="${tier.minDonation}" data-tier-id="${tier.id}">Select Reward</button>
            </div>
        `).join('');

        const voterEmail = getCurrentUser()?.email || '';
        const updatesHtml = renderUpdatesListHtml(project.updates, this, project.id, voterEmail);
        const tab = this.detailTab || 'story';

        const supporterHtml = (project.comments || []).filter((c) => c.message).length > 0
            ? (project.comments || []).filter((c) => c.message).map(comment => `
            <div class="fg-fund-comment-item">
                <div class="fg-fund-comment-item__header">
                    <span class="fg-fund-comment-item__author">${comment.donorName}</span>
                    <span class="fg-fund-comment-item__amount">Pledged €${comment.amount}</span>
                </div>
                <p class="fg-fund-comment-item__message">${comment.message}</p>
                <span class="fg-fund-comment-item__date">${comment.timestamp}</span>
            </div>
        `).join('')
            : `<p class="fg-fund-empty-sub">Be the first to leave a supportive message!</p>`;
        const communityHtml = renderCommunityCommentsHtml(project, this, {
            canModerate: !!isOwner || canManageCampaign(project, getCurrentUser()),
        });
        const commentsHtml = `
            <h4 class="fg-fund-comments-subhead">${this.tr('communityComments')}</h4>
            ${communityHtml}
            <h4 class="fg-fund-comments-subhead">${this.tr('supporterMessages')}</h4>
            <div class="fg-fund-detail__supporters">${supporterHtml}</div>`;

        const noindexMeta = this.previewMode ? '<meta name="robots" content="noindex,nofollow">' : '';

        return `
            <div class="fg-fund-detail animate-fade-in" ${this.previewMode ? 'data-preview-page' : ''}>
                ${noindexMeta}
                ${previewBanner}
                <button class="fg-fund-back-link" id="go-back-browse-link">${this.tr('backBrowse')}</button>

                ${galleryHtml}

                <nav class="fg-fund-detail-tabs" role="tablist">
                    <button type="button" class="fg-fund-detail-tabs__btn ${tab === 'story' ? 'active' : ''}" data-detail-tab="story">${this.tr('tabStory')}</button>
                    <button type="button" class="fg-fund-detail-tabs__btn ${tab === 'updates' ? 'active' : ''}" data-detail-tab="updates">${this.tr('tabUpdates')}</button>
                    ${(project.faqs || []).length ? `<button type="button" class="fg-fund-detail-tabs__btn ${tab === 'faq' ? 'active' : ''}" data-detail-tab="faq">${this.tr('tabFaq')}</button>` : ''}
                    <button type="button" class="fg-fund-detail-tabs__btn ${tab === 'comments' ? 'active' : ''}" data-detail-tab="comments">${this.tr('tabComments')}</button>
                </nav>

                <header class="fg-fund-detail__hero" style="background-image: ${heroBg};">
                    <div class="fg-fund-detail__hero-overlay">
                        <span class="fg-fund-detail__tag">${project.category}</span>
                        <h1 class="fg-fund-detail__hero-title">${project.title}</h1>
                        <p class="fg-fund-detail__hero-creator">By <strong>${project.creator}</strong>
                            ${project.kycVerified ? `<span class="fg-fund-kyc-badge">${this.tr('kycVerified')}</span>` : ''}
                            ${project.stripeVerified ? `<span class="fg-fund-verified-badge">${this.tr('verifiedCreator')}</span>` : ''}
                        </p>
                        <div class="fg-fund-detail__progress fg-fund-detail__progress--hero">
                            <div class="fg-fund-detail__progress-bar">
                                <div class="fg-fund-detail__progress-fill" style="width: ${pct}%"></div>
                            </div>
                        </div>
                        <div class="fg-fund-detail__stats-row">
                            <div class="fg-fund-detail__stat-pill"><strong>€${project.raised.toLocaleString()}</strong><span>raised</span></div>
                            <div class="fg-fund-detail__stat-pill"><strong>€${project.target.toLocaleString()}</strong><span>target</span></div>
                            <div class="fg-fund-detail__stat-pill"><strong>${pct}%</strong><span>funded</span></div>
                            <div class="fg-fund-detail__stat-pill"><strong>${donorsCount}</strong><span>donors</span></div>
                            <div class="fg-fund-detail__stat-pill"><strong>${daysLeft}</strong><span>days left</span></div>
                        </div>
                    </div>
                </header>

                <div class="fg-fund-detail__layout">
                    <!-- Main Content Column -->
                    <div class="fg-fund-detail__main">
                        ${project.videoURL ? `
                            <div class="fg-fund-detail__video">
                                <iframe src="${project.videoURL}" title="Campaign video" allowfullscreen loading="lazy"></iframe>
                            </div>
                        ` : ''}

                        ${countdownHtml}
                        
                        <div class="fg-fund-detail__section ${tab === 'story' ? '' : 'hidden'}" data-panel="story">
                            <h3>${this.tr('aboutProject')}</h3>
                            <div class="fg-fund-rich-body">${project.bodyHtml || fullText}</div>
                        </div>

                        <div class="fg-fund-detail__section ${tab === 'updates' ? '' : 'hidden'}" data-panel="updates">
                            <h3>${this.tr('updates')}</h3>
                            <div class="fg-fund-detail__updates">${updatesHtml}</div>
                        </div>

                        ${(project.faqs || []).length ? `
                        <div class="fg-fund-detail__section ${tab === 'faq' ? '' : 'hidden'}" data-panel="faq">
                            ${faqHtml}
                        </div>` : ''}

                        <div class="fg-fund-detail__section ${tab === 'comments' ? '' : 'hidden'}" id="fund-comments-section" data-panel="comments">
                            <h3>${this.tr('comments')}</h3>
                            <div class="fg-fund-detail__comments">${commentsHtml}</div>
                        </div>
                    </div>

                    <!-- Sidebar Actions Column -->
                    <aside class="fg-fund-detail__sidebar">
                        <div class="fg-fund-detail__stats-card">
                            <button class="fg-fund-btn fg-fund-btn--primary fg-fund-btn--donate" id="detail-donate-btn" ${closed || this.previewMode ? 'disabled' : ''}>${closed ? this.tr('closed') : this.tr('donate')}</button>
                            ${(isOwner || canViewAnalytics(project, getCurrentUser())) && !this.previewMode ? `
                                <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="fund-analytics-btn">${this.tr('manageCampaign')}</button>
                                <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="close-campaign-btn">${this.tr('closeCampaign')}</button>
                            ` : ''}
                            
                            <div class="fg-fund-detail__share-container">
                                <span class="fg-fund-detail__share-title">${this.tr('share')}</span>
                                <div class="fg-fund-detail__share-buttons">
                                    <button class="fg-fund-share-btn" id="share-copy">${this.tr('copyLink')}</button>
                                    <button class="fg-fund-share-btn" id="share-twitter">${this.tr('shareX')}</button>
                                    <button class="fg-fund-share-btn" id="share-whatsapp">${this.tr('shareWhatsApp')}</button>
                                </div>
                            </div>
                        </div>

                        ${privateBanner}
                        ${visibleTiers.length > 0 ? `
                            <div class="fg-fund-detail__rewards-section">
                                <h3>${this.tr('rewards')}</h3>
                                ${rewardTiersHtml}
                            </div>
                        ` : ''}
                    </aside>
                </div>
            </div>
        `;
    }

    // ==========================================
    // VIEW 3: CREATE PROJECT FORM WIZARD
    // ==========================================
    renderCreate() {
        let stepContent = '';
        
        switch (this.formStep) {
            case 1:
                stepContent = this.renderWizardStep1();
                break;
            case 2:
                stepContent = this.renderWizardStep2();
                break;
            case 3:
                stepContent = this.renderWizardStep3();
                break;
            case 4:
                stepContent = this.renderWizardStep4();
                break;
            default:
                stepContent = this.renderWizardStep1();
        }

        const stepsHeader = `
            <div class="fg-fund-create__stepper">
                <div class="fg-fund-create__step ${this.formStep >= 1 ? 'active' : ''}"><span>1</span> ${this.tr('stepBasics')}</div>
                <div class="fg-fund-create__step-line ${this.formStep >= 2 ? 'active' : ''}"></div>
                <div class="fg-fund-create__step ${this.formStep >= 2 ? 'active' : ''}"><span>2</span> ${this.tr('stepStory')}</div>
                <div class="fg-fund-create__step-line ${this.formStep >= 3 ? 'active' : ''}"></div>
                <div class="fg-fund-create__step ${this.formStep >= 3 ? 'active' : ''}"><span>3</span> ${this.tr('stepPerks')}</div>
                <div class="fg-fund-create__step-line ${this.formStep >= 4 ? 'active' : ''}"></div>
                <div class="fg-fund-create__step ${this.formStep >= 4 ? 'active' : ''}"><span>4</span> ${this.tr('stepReview')}</div>
            </div>
        `;

        return `
            <div class="fg-fund-create animate-fade-in">
                <button class="fg-fund-back-link" id="wizard-cancel">← Back</button>
                <div class="fg-fund-create__header">
                    <h2>${this.tr('launchTitle')}</h2>
                    <p>${this.tr('launchSub')}</p>
                    <span id="fg-fund-saved-stamp" class="fg-fund-saved-stamp"></span>
                    <div class="fg-fund-create__toolbar">
                        <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="wizard-preview-btn">${this.tr('preview')} ${infoIcon('preview', this)}</button>
                        <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="wizard-share-review-btn">${this.tr('shareReview')} ${infoIcon('shareReview', this)}</button>
                    </div>
                    ${this._reviewShareUrl ? `<p class="fg-fund-review-link-hint">${this.tr('shareReviewHint')}<br><code class="fg-fund-review-link">${this._reviewShareUrl}</code>
                        <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="copy-review-link">${this.tr('copyReviewLink')}</button>
                        <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="regen-review-link">${this.tr('regenerateReview')}</button></p>` : ''}
                </div>

                ${stepsHeader}

                <div class="fg-fund-create__body">
                    ${stepContent}
                </div>
            </div>
        `;
    }

    renderConfirmation() {
        const project = this.projects.find(p => p.id === this.newProjectId);
        const title = project?.title || this.newProject.title || 'Your project';
        const shareUrl = getCampaignShareUrl(this.newProjectId);

        return `
            <div class="fg-fund-confirm animate-fade-in">
                <div class="fg-fund-confirm__icon">🎉</div>
                <h2 class="fg-fund-confirm__title">Your project is live!</h2>
                <p class="fg-fund-confirm__project">${title}</p>
                <div class="fg-fund-confirm__actions">
                    <button class="fg-fund-btn fg-fund-btn--primary" id="conf-view-campaign">View Project</button>
                    <button class="fg-fund-btn" id="conf-go-home">Back to Browse</button>
                </div>
                <div class="fg-fund-confirm__share">
                    <span>Share your campaign</span>
                    <div class="fg-fund-confirm__share-buttons">
                        <button class="fg-fund-share-btn" id="conf-share-copy" data-url="${shareUrl}">Copy link</button>
                        <button class="fg-fund-share-btn" id="conf-share-twitter" data-url="${shareUrl}">Share on X</button>
                        <button class="fg-fund-share-btn" id="conf-share-whatsapp" data-url="${shareUrl}">WhatsApp</button>
                    </div>
                </div>
            </div>
        `;
    }

    renderWizardStep1() {
        const categoriesOptions = this.categories.filter(c => c !== 'All').map(c => `
            <option value="${c}" ${this.newProject.category === c ? 'selected' : ''}>${c}</option>
        `).join('');

        return `
            <form id="wizard-form-1" class="fg-fund-form">
                <div class="fg-field">
                    <label for="p-title">${labelWithHelp('Project Title', 'title', this)}</label>
                    <input type="text" id="p-title" class="fg-input" placeholder="e.g. Grassroots Skills Camp for Girls" value="${this.newProject.title}" required minlength="5">
                </div>

                <div class="fg-field">
                    <label for="p-category">${labelWithHelp('Category', 'category', this)}</label>
                    <select id="p-category" class="fg-select">
                        ${categoriesOptions}
                    </select>
                </div>

                <div class="fg-field">
                    <label for="p-short-desc">${labelWithHelp('Short Description (max 150)', 'shortDesc', this)}</label>
                    <input type="text" id="p-short-desc" class="fg-input" placeholder="Write a catchy summary..." value="${this.newProject.shortDesc}" required maxlength="150">
                    <small id="char-count" class="fg-fund-char-count">150 characters remaining</small>
                </div>

                <div class="fg-fund-wizard__actions">
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary fg-fund-btn--wizard-next">Continue →</button>
                </div>
            </form>
        `;
    }

    renderWizardStep2() {
        const initial = this.newProject.bodyHtml || this.newProject.fullDesc || '';
        return `
            <form id="wizard-form-2" class="fg-fund-form">
                <div class="fg-field">
                    <label>${labelWithHelp(this.tr('storyEditor'), 'storyEditor', this)}</label>
                    ${renderRichEditorHtml(initial)}
                </div>
                <div class="fg-fund-wizard__actions">
                    <button type="button" class="fg-fund-btn" id="wizard-prev-2">Back</button>
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary fg-fund-btn--wizard-next">Continue →</button>
                </div>
            </form>
        `;
    }

    renderWizardStep3() {
        const minEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const maxEnd = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const defaultEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const endDateVal = this.newProject.endDate || defaultEnd;
        const galleryVal = (this.newProject.images || []).join('\n');

        const addedTiersHtml = this.newProject.rewardTiers.map((tier, idx) => `
            <div class="fg-fund-wizard__added-tier">
                <div class="fg-fund-wizard__added-tier-head">
                    <strong>${tier.name} (€${tier.minDonation}+)</strong>
                    <button type="button" class="fg-fund-btn fg-fund-btn--danger-text remove-tier-btn" data-index="${idx}">Remove</button>
                </div>
                <p>${tier.description}</p>
                ${tier.estimatedDelivery ? `<span class="fg-fund-tier-delivery">${this.tr('deliveryEst')}: ${tier.estimatedDelivery}</span>` : ''}
            </div>
        `).join('');

        const faqRows = (this.newProject.faqs || []).map((f, i) => `
            <div class="fg-fund-faq-draft" data-faq-i="${i}">
                <input type="text" class="fg-input faq-q" value="${f.question}" placeholder="Question">
                <textarea class="fg-textarea faq-a" placeholder="Answer">${f.answer}</textarea>
            </div>
        `).join('');

        return `
            <form id="wizard-form-3" class="fg-fund-form">
                <div class="fg-field">
                    <label for="p-target">${labelWithHelp('Funding target (€)', 'target', this)}</label>
                    <input type="number" id="p-target" class="fg-input" min="2000" value="${this.newProject.target}" required>
                </div>
                <div class="fg-field">
                    <label for="p-end-date">${labelWithHelp('End date', 'endDate', this)}</label>
                    <input type="date" id="p-end-date" class="fg-input" value="${endDateVal}" min="${minEnd}" max="${maxEnd}" required>
                </div>
                <div class="fg-fund-wizard__tiers-section">
                    <h3>${labelWithHelp('Reward tiers', 'rewardTiers', this)}</h3>
                    <div id="added-tiers-list">${addedTiersHtml}</div>
                    ${this.newProject.rewardTiers.length < 10 ? `
                    <div class="fg-fund-wizard__add-tier-form">
                        <input type="text" id="tier-name" class="fg-input" placeholder="Tier name">
                        <input type="number" id="tier-min" class="fg-input" placeholder="Min €" min="1">
                        <textarea id="tier-desc" class="fg-textarea" placeholder="Description"></textarea>
                        <label for="tier-delivery">${labelWithHelp(this.tr('tierDelivery'), 'tierDelivery', this)}</label>
                        <select id="tier-delivery" class="fg-select">
                            <option value="">— ${this.tr('noDelivery')} —</option>
                            ${monthYearOptions()}
                        </select>
                        <label class="fg-fund-check-row">
                            <input type="checkbox" id="tier-private">
                            ${labelWithHelp(this.tr('tierPrivate'), 'secretTier', this)}
                        </label>
                        <input type="number" id="tier-quantity" class="fg-input" min="0" placeholder="Quantity limit (optional)">
                        <button type="button" class="fg-fund-btn" id="add-tier-action">Add tier</button>
                    </div>` : ''}
                </div>
                <div class="fg-field">
                    <label>${labelWithHelp('FAQ', 'faq', this)}</label>
                    <div id="faq-draft-list">${faqRows}</div>
                    <button type="button" class="fg-fund-btn fg-fund-btn--ghost" id="add-faq-btn">+ FAQ</button>
                </div>
                <div class="fg-field">
                    <label for="p-gallery">${labelWithHelp('Gallery (up to 8 URLs)', 'gallery', this)}</label>
                    <textarea id="p-gallery" class="fg-textarea" placeholder="One image URL per line">${galleryVal}</textarea>
                    <label class="fg-fund-upload-fallback">
                        <input type="file" id="p-gallery-file" accept="image/*" multiple hidden>
                        Tap to add photos (stored as preview URLs)
                    </label>
                </div>
                <div class="fg-field">
                    <label for="p-image">${labelWithHelp('Hero image URL', 'gallery', this)}</label>
                    <input type="url" id="p-image" class="fg-input" value="${this.newProject.imageURL}">
                </div>
                <div class="fg-field">
                    <label for="p-video">${labelWithHelp('Video embed', 'video', this)}</label>
                    <input type="url" id="p-video" class="fg-input" value="${this.newProject.videoURL}">
                </div>
                <label class="fg-fund-check-row">
                    <input type="checkbox" id="p-coming-soon" ${this.newProject.comingSoonEnabled ? 'checked' : ''}>
                    ${labelWithHelp(this.tr('enableComingSoon'), 'comingSoon', this)}
                </label>
                <div class="fg-fund-wizard__actions">
                    <button type="button" class="fg-fund-btn" id="wizard-prev-3">Back</button>
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary">Continue →</button>
                </div>
            </form>
        `;
    }

    renderComingSoon() {
        const project = this.projects.find((p) => p.id === this.selectedProjectId)
            || loadDraftsForUser().find((d) => d.draftId === this.selectedProjectId);
        if (!project) {
            return `<div class="fg-fund-error">Campaign not found.</div>`;
        }
        const hero = project.imageURL || project.images?.[0] || '';
        return `
            <div class="fg-fund-coming animate-fade-in">
                <div class="fg-fund-coming__hero" style="${hero ? `background-image:url('${hero}')` : ''}"></div>
                <div class="fg-fund-coming__card">
                    <span class="fg-fund-coming__pill">${this.tr('comingSoonTitle')}</span>
                    <h1>${project.title || 'Upcoming campaign'}</h1>
                    <p>${project.shortDesc || project.description || ''}</p>
                    <form id="follower-form" class="fg-fund-form">
                        <label for="follower-email">${this.tr('notifyMe')}</label>
                        <input type="email" id="follower-email" class="fg-input" required placeholder="you@example.com">
                        <button type="submit" class="fg-fund-btn fg-fund-btn--primary">${this.tr('notifyMe')}</button>
                    </form>
                </div>
            </div>`;
    }

    renderAnalyticsPanel(project) {
        const clicks = getLocalClicksFromModule(project.id);
        const bySource = aggregateClicksBySource(clicks);
        const byDay = aggregateDonationsByDay(project.comments || []);
        const utm = this._utmLinks || buildUtmLinks(project.id, project.title);
        const views = getCampaignViews(project.id);
        const topUpTotal = getTopUpTotals(project.comments);
        const backers = getBackerFeed(project);
        const backerRows = backers.length
            ? backers.map((b) => `
                <tr>
                    <td>${b.date}</td>
                    <td>${b.name}</td>
                    <td>€${(b.amount || 0).toFixed(2)}</td>
                    <td>€${(b.tierAmount || 0).toFixed(2)} + €${(b.topUpAmount || 0).toFixed(2)} top-up</td>
                </tr>`).join('')
            : '<tr><td colspan="4">No pledges yet.</td></tr>';

        return `
            <div class="fg-fund-analytics animate-fade-in">
                <button class="fg-fund-back-link" id="go-back-detail-link">${this.tr('backBrowse')}</button>
                <h2>${this.tr('analyticsTitle')}</h2>
                <p class="fg-fund-analytics__campaign">${project.title}</p>
                <div class="fg-fund-analytics__grid">
                    <div class="fg-fund-stat-card">
                        <span>${this.tr('analyticsViews')}</span>
                        <strong>${views}</strong>
                    </div>
                    <div class="fg-fund-stat-card">
                        <span>${this.tr('topUpTotal')}</span>
                        <strong>€${topUpTotal.toFixed(2)}</strong>
                    </div>
                </div>
                <section class="fg-fund-analytics__section">
                    <h3>${this.tr('pollResults')}</h3>
                    ${renderPollResultsDashboard(project.updates, project.id, this)}
                </section>
                <section class="fg-fund-analytics__section">
                    <h3>${this.tr('utmTitle')} ${infoIcon('analytics', this)}</h3>
                    <div class="fg-fund-utm-links">
                        <div class="fg-fund-utm-row"><span>WhatsApp</span><button type="button" class="fg-fund-btn fg-fund-btn--ghost" data-copy-utm="whatsapp">${this.tr('copyUtm')}</button></div>
                        <div class="fg-fund-utm-row"><span>Instagram</span><button type="button" class="fg-fund-btn fg-fund-btn--ghost" data-copy-utm="instagram">${this.tr('copyUtm')}</button></div>
                        <div class="fg-fund-utm-row"><span>Direct</span><button type="button" class="fg-fund-btn fg-fund-btn--ghost" data-copy-utm="direct">${this.tr('copyUtm')}</button></div>
                    </div>
                </section>
                <section class="fg-fund-analytics__section">
                    <h3>${this.tr('analyticsClicks')}</h3>
                    ${renderBarChart(bySource)}
                </section>
                <section class="fg-fund-analytics__section">
                    <h3>${this.tr('analyticsDonations')}</h3>
                    ${renderBarChart(byDay)}
                </section>
                <section class="fg-fund-analytics__section">
                    <h3>${this.tr('backerFeed')}</h3>
                    <table class="fg-fund-table">
                        <thead><tr><th>Date</th><th>Backer</th><th>Total</th><th>Breakdown</th></tr></thead>
                        <tbody>${backerRows}</tbody>
                    </table>
                </section>
                ${this.renderKycSection(project)}
                ${this.renderPostUpdateSection(project)}
                ${this.renderTierFulfillmentSection(project)}
                ${this.renderTeamSection(project)}
            </div>`;
    }

    renderKycSection(project) {
        const user = getCurrentUser();
        if (!user || project.ownerUid !== user.uid) return '';
        const verified = this.creatorKyc?.kycVerified;
        return `
            <section class="fg-fund-analytics__section fg-fund-kyc-section">
                <h3>${this.tr('kycTitle')} ${infoIcon('kyc', this)}</h3>
                <p class="fg-fund-empty-sub">${this.tr('kycSub')}</p>
                <span class="fg-fund-connect__badge">${verified ? this.tr('kycVerified') : this.tr('kycPending')}</span>
                ${verified ? '' : `<button type="button" class="fg-fund-btn fg-fund-btn--primary" id="fund-kyc-btn">${this.tr('kycBtn')}</button>`}
            </section>`;
    }

    renderTierFulfillmentSection(project) {
        const user = getCurrentUser();
        if (!canManageCampaign(project, user) || !(project.rewardTiers || []).length) return '';
        const rows = (project.rewardTiers || []).map((tier) => {
            const backers = getBackersForTier(project, tier.id).length;
            const secretLink = tier.isPrivate && tier.secretToken
                ? `<p class="fg-fund-secret-link"><code>${buildSecretTierUrl(project.id, tier.secretToken)}</code>
                    <button type="button" class="fg-fund-btn fg-fund-btn--ghost" data-copy-secret="${tier.secretToken}">${this.tr('copySecretLink')}</button></p>`
                : '';
            return `
                <div class="fg-fund-tier-fulfill-row">
                    <div>
                        <strong>${tier.name}${tier.isPrivate ? ' 🔒' : ''}</strong>
                        ${renderTierDeliveryHtml(tier, this)}
                        <span class="fg-fund-tier-fulfill-row__meta">${backers} backer(s)</span>
                        ${secretLink}
                    </div>
                    ${tier.fulfilled
                        ? `<span class="fg-fund-tier-delivery fg-fund-tier-delivery--done">${this.tr('tierFulfilled')}</span>`
                        : `<button type="button" class="fg-fund-btn fg-fund-btn--ghost" data-mark-fulfilled="${tier.id}">${this.tr('markFulfilled')}</button>`}
                </div>`;
        }).join('');
        return `
            <section class="fg-fund-analytics__section">
                <h3>${this.tr('tierFulfillment')}</h3>
                <div class="fg-fund-tier-fulfill-list">${rows}</div>
            </section>`;
    }

    renderPostUpdateSection(project) {
        const user = getCurrentUser();
        if (!canManageCampaign(project, user)) return '';
        const notifyChecked = project.notifyOnComment !== false ? 'checked' : '';
        return `
            <section class="fg-fund-analytics__section fg-fund-post-update">
                <h3>${this.tr('postUpdateTitle')} ${infoIcon('postUpdate', this)}</h3>
                <form id="post-update-form" class="fg-fund-form">
                    <div class="fg-field">
                        <label for="update-title">${this.tr('updateTitleLabel')}</label>
                        <input type="text" id="update-title" class="fg-input" required>
                    </div>
                    <div class="fg-field">
                        <label>${this.tr('updateBodyLabel')}</label>
                        ${renderRichEditorHtml('')}
                    </div>
                    <div class="fg-field">
                        <label for="update-image">${this.tr('updateImageLabel')}</label>
                        <input type="url" id="update-image" class="fg-input" placeholder="https://">
                    </div>
                    <label class="fg-fund-check-row">
                        <input type="checkbox" id="update-add-poll">
                        ${labelWithHelp(this.tr('addPoll'), 'poll', this)}
                    </label>
                    <div id="update-poll-fields" class="fg-fund-poll-fields hidden">
                        <div class="fg-field">
                            <label for="poll-question">${this.tr('pollQuestion')}</label>
                            <input type="text" id="poll-question" class="fg-input">
                        </div>
                        <div class="fg-field">
                            <label for="poll-options">${this.tr('pollOptions')}</label>
                            <textarea id="poll-options" class="fg-textarea" rows="4"></textarea>
                        </div>
                    </div>
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary">${this.tr('sendUpdate')}</button>
                </form>
                <label class="fg-fund-check-row fg-fund-notify-comment">
                    <input type="checkbox" id="notify-on-comment" ${notifyChecked}>
                    ${this.tr('notifyOnComment')}
                </label>
            </section>`;
    }

    renderTeamSection(project) {
        const user = getCurrentUser();
        if (!user || project.ownerUid !== user.uid) return '';
        const collabs = getCollaboratorsLocal(project.id);
        const rows = collabs.length
            ? collabs.map((c) => `
                <div class="fg-fund-team-row">
                    <span>${c.email} · ${c.role} · ${c.status}</span>
                    <button type="button" class="fg-fund-btn fg-fund-btn--danger-text" data-revoke-collab="${c.email}">${this.tr('revokeAccess')}</button>
                </div>`).join('')
            : '<p class="fg-fund-empty-sub">No collaborators yet.</p>';
        return `
            <section class="fg-fund-analytics__section">
                <h3>${this.tr('teamTitle')} ${infoIcon('team', this)}</h3>
                <form id="team-invite-form" class="fg-fund-form fg-fund-team-invite">
                    <input type="email" id="collab-email" class="fg-input" placeholder="${this.tr('inviteEmail')}" required>
                    <select id="collab-role" class="fg-select">
                        <option value="editor">${this.tr('roleEditor')}</option>
                        <option value="viewer">${this.tr('roleViewer')}</option>
                    </select>
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary">${this.tr('sendInvite')}</button>
                </form>
                <div class="fg-fund-team-list">${rows}</div>
            </section>`;
    }

    renderAnalytics() {
        const project = this.projects.find((p) => p.id === this.selectedProjectId);
        if (!project) {
            return `<div class="fg-fund-error">Campaign not found.</div>`;
        }
        const user = getCurrentUser();
        if (!canViewAnalytics(project, user)) {
            return `<div class="fg-fund-error">Private analytics — owners and team only.</div>`;
        }
        this._utmLinks = buildUtmLinks(project.id, project.title);
        return this.renderAnalyticsPanel(project);
    }

    renderWizardStep4() {
        const feeHtml = renderFeeVisualizer(this.newProject.target, this);
        return `
            <form id="wizard-form-4" class="fg-fund-form">
                <div class="fg-fund-review-card">
                    <h3>${this.newProject.title || 'Campaign preview'}</h3>
                    <p>${this.newProject.shortDesc}</p>
                </div>
                ${feeHtml}
                ${this.renderConnectPanel()}
                ${!this.canPublishCampaign() ? `<p class="fg-fund-publish-hint">${this.tr('publishRequiresStripe')}</p>` : ''}
                <div class="fg-field">
                    <label for="p-creator">${labelWithHelp('Creator name', 'creator', this)}</label>
                    <input type="text" id="p-creator" class="fg-input" value="${this.newProject.creatorName}" required>
                </div>
                <div class="fg-field">
                    <label for="p-email">${labelWithHelp('Contact email', 'email', this)}</label>
                    <input type="email" id="p-email" class="fg-input" value="${this.newProject.email}" required>
                </div>
                <div class="fg-fund-wizard__actions">
                    <button type="button" class="fg-fund-btn" id="wizard-prev-4">Back</button>
                    <button type="submit" class="fg-fund-btn fg-fund-btn--primary">${this.tr('publish')} ${infoIcon('publish', this)}</button>
                </div>
            </form>
        `;
    }

    // ==========================================
    // VIEW 4: DONATE FLOW
    // ==========================================
    renderDonate() {
        const project = this.projects.find(p => p.id === this.selectedProjectId);
        if (!project) return `<div class="fg-fund-error">Campaign not found. <button class="fg-fund-btn" id="go-back-browse">Back to Browse</button></div>`;

        if (project.ownerUid && (!project.stripeAccountId || !project.stripeVerified)) {
            return `<div class="fg-fund-error animate-fade-in">
                <p>${this.tr('donateRequiresStripe')}</p>
                <button class="fg-fund-btn" id="go-back-detail-link">${this.tr('backBrowse')}</button>
            </div>`;
        }

        const visibleTiers = getPublicRewardTiers(project, this.secretTierToken);
        const matchedTier = visibleTiers.find(t => t.id === this.selectedRewardTierId);
        const tierMatchHtml = visibleTiers.length > 0 ? `
            <div class="fg-fund-donate__tier-match">
                ${matchedTier
                    ? `<strong>Reward unlocked:</strong> ${matchedTier.name} — ${matchedTier.description}`
                    : '<strong>No reward tier</strong> at this amount. Increase your pledge to unlock a tier.'}
            </div>
        ` : '';

        return `
            <div class="fg-fund-donate animate-fade-in">
                <button class="fg-fund-back-link" id="go-back-detail-link">← Cancel and Back</button>
                
                <div class="fg-fund-donate__header">
                    <h2>Support this Campaign</h2>
                    <p>You are supporting: <strong>${project.title}</strong></p>
                </div>

                <div class="fg-fund-donate__layout">
                    <!-- Donation Form Column -->
                    <div class="fg-fund-donate__main">
                        <form id="donation-submit-form" class="fg-fund-form">
                            <!-- Amount Selection -->
                            <div class="fg-fund-donate__amount-box">
                                <h3>Select Pledge Amount</h3>
                                <div class="fg-fund-donate__amount-presets">
                                    <button type="button" class="fg-fund-amount-btn ${this.donationAmount === 5 ? 'active' : ''}" data-val="5">€5</button>
                                    <button type="button" class="fg-fund-amount-btn ${this.donationAmount === 10 ? 'active' : ''}" data-val="10">€10</button>
                                    <button type="button" class="fg-fund-amount-btn ${this.donationAmount === 25 ? 'active' : ''}" data-val="25">€25</button>
                                    <button type="button" class="fg-fund-amount-btn ${this.donationAmount === 50 ? 'active' : ''}" data-val="50">€50</button>
                                    <button type="button" class="fg-fund-amount-btn ${this.donationAmount === 100 ? 'active' : ''}" data-val="100">€100</button>
                                </div>
                                <div class="fg-field" style="margin-top:12px;">
                                    <label for="custom-amount-input">Custom Amount (€)</label>
                                    <input type="number" id="custom-amount-input" class="fg-input" value="${this.donationAmount}" min="5" required>
                                </div>
                            </div>

                            ${tierMatchHtml}

                            <!-- Supporter Info -->
                            <div class="fg-fund-donate__info-section">
                                <h3>Your Information</h3>
                                <div class="fg-field">
                                    <label for="donor-email">Email Address (for receipts)</label>
                                    <input type="email" id="donor-email" class="fg-input" placeholder="you@example.com" value="${this.donorEmail}" required>
                                </div>
                                <div class="fg-field">
                                    <label for="donor-name">Backer Name (Optional)</label>
                                    <input type="text" id="donor-name" class="fg-input" placeholder="Anonymous" value="${this.donorName}">
                                </div>
                                <div class="fg-field">
                                    <label for="donor-message">Support Message (Optional, Max 200 Characters)</label>
                                    <input type="text" id="donor-message" class="fg-input" placeholder="Wish the organizers luck!" value="${this.donorMessage}" maxlength="200">
                                </div>
                                <div class="fg-field">
                                    <label for="donation-topup">${labelWithHelp(this.tr('topUp'), 'topUp', this)}</label>
                                    <input type="number" id="donation-topup" class="fg-input" min="0" step="1" value="${this.donationTopUp || 0}" placeholder="0">
                                </div>
                                <label class="fg-fund-check-row">
                                    <input type="checkbox" id="hide-donor-name" ${this.hideDonorName ? 'checked' : ''}>
                                    ${labelWithHelp(this.tr('hideName'), 'hideName', this)}
                                </label>
                            </div>

                            <!-- Stripe Credit Card section -->
                            <div class="fg-fund-donate__stripe-box">
                                <h3>Credit / Debit Card</h3>
                                <div id="stripe-card-element-container" style="margin-bottom:16px;">
                                    <!-- Stripe Elements card form loads here -->
                                    <div id="stripe-card-element" style="padding:12px; border:2px solid var(--border-color); border-radius:10px; background:var(--card-bg);"></div>
                                    <div id="stripe-errors" class="fg-stripe-error" style="color:red; font-size:0.85rem; margin-top:6px; font-weight:700;"></div>
                                </div>

                                <!-- Custom Mock Card inputs as fallback if Stripe.js fails or is in offline test mode -->
                                <div id="simulated-card-form" style="display:none; padding:16px; border:2px solid var(--border-color); border-radius:12px; background:var(--hover-bg);">
                                    <p style="margin:0 0 12px; font-size:0.8rem; font-weight:700; color:var(--text-secondary);">⚠️ Simulated Stripe Terminal (Mock Mode Active)</p>
                                    <div class="fg-field">
                                        <label>Card Number</label>
                                        <input type="text" class="fg-input mock-card-input" placeholder="4242 4242 4242 4242" maxlength="19">
                                    </div>
                                    <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px;">
                                        <div class="fg-field">
                                            <label>Expiry Date</label>
                                            <input type="text" class="fg-input mock-expiry-input" placeholder="MM/YY" maxlength="5">
                                        </div>
                                        <div class="fg-field">
                                            <label>CVC</label>
                                            <input type="text" class="fg-input mock-cvc-input" placeholder="123" maxlength="3">
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" class="fg-fund-btn fg-fund-btn--primary fg-fund-btn--pay-submit" ${this.paymentIsProcessing ? 'disabled' : ''}>
                                ${this.paymentIsProcessing ? '<span class="fg-fund-spinner"></span> Processing...' : 'Pay with Card'}
                            </button>
                        </form>
                    </div>

                    <!-- Sidebar Summary Column -->
                    <aside class="fg-fund-donate__sidebar">
                        <div class="fg-fund-donate__summary-card">
                            <h3>Pledge Summary</h3>
                            <div class="fg-fund-donate__summary-row">
                                <span>Support pledge:</span>
                                <strong>€${this.donationAmount}</strong>
                            </div>
                            <div class="fg-fund-donate__summary-row" style="border:none;">
                                <span>Selected reward:</span>
                                <strong>${this.getRewardName(project)}</strong>
                            </div>
                            <div class="fg-fund-donate__summary-total">
                                <span>Total Contribution</span>
                                <span>€${this.donationAmount}</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        `;
    }

    renderDonateSuccess() {
        const donorLabel = this.donorName && this.donorName !== 'Anonymous' ? this.donorName : null;
        const messageBlock = this.donorMessage.trim()
            ? `<p class="fg-fund-donate__success-message">"${this.donorMessage}"</p>`
            : '';

        return `
            <div class="fg-fund-donate fg-fund-donate--success animate-fade-in">
                <div class="fg-fund-donate__success-icon">✓</div>
                <h2>Thank you! Your donation of €${this.donationAmount} has been received.</h2>
                ${donorLabel ? `<p class="fg-fund-donate__success-donor">Thank you, <strong>${donorLabel}</strong>.</p>` : ''}
                ${messageBlock}
                <div class="fg-fund-donate__success-actions">
                    <button class="fg-fund-btn fg-fund-btn--primary" id="success-view-project">View project</button>
                    <button class="fg-fund-btn" id="success-go-browse">Back to browse</button>
                </div>
            </div>
        `;
    }

    getRewardName(project) {
        if (!this.selectedRewardTierId) return 'No Reward';
        const tier = project.rewardTiers.find(t => t.id === this.selectedRewardTierId);
        return tier ? tier.name : 'No Reward';
    }

    // ==========================================
    // DYNAMIC STRIPE.JS LOADER
    // ==========================================
    loadStripe() {
        const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
        if (!stripeKey) {
            requestAnimationFrame(() => this.showSimulatedCardForm());
            return;
        }

        const stripeEl = document.getElementById('stripe-card-element-container');
        const simulatedEl = document.getElementById('simulated-card-form');
        if (stripeEl) stripeEl.style.display = '';
        if (simulatedEl) simulatedEl.style.display = 'none';

        if (window.Stripe) {
            this.initStripeElements();
            return;
        }

        const scriptId = 'stripe-cdn-script';
        const existing = document.getElementById(scriptId);
        if (existing) {
            if (window.Stripe) this.initStripeElements();
            else existing.addEventListener('load', () => this.initStripeElements(), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://js.stripe.com/v3/';
        script.onload = () => this.initStripeElements();
        script.onerror = () => {
            console.warn('Failed to load Stripe.js from CDN. Falling back to simulated Stripe mode.');
            this.showSimulatedCardForm();
        };
        document.head.appendChild(script);
    }

    initStripeElements() {
        const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
        if (!stripeKey) {
            this.showSimulatedCardForm();
            return;
        }

        const mountEl = document.getElementById('stripe-card-element');
        if (!mountEl) return;

        try {
            if (this.stripeCard) {
                this.stripeCard.destroy();
                this.stripeCard = null;
            }
            this.stripeInstance = window.Stripe(stripeKey);
            this.stripeElements = this.stripeInstance.elements();
            this.stripeCard = this.stripeElements.create('card', {
                style: {
                    base: {
                        color: document.body.getAttribute('data-theme') === 'dark' ? '#ffffff' : '#333333',
                        fontFamily: 'Nunito, sans-serif',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#a0aec0'
                        }
                    }
                }
            });
            this.stripeCard.mount('#stripe-card-element');
            this.stripeCard.on('change', (event) => {
                const displayError = document.getElementById('stripe-errors');
                if (displayError) {
                    displayError.textContent = event.error ? event.error.message : '';
                }
            });
        } catch (e) {
            console.error('Failed to initialize Stripe Elements:', e);
            this.showSimulatedCardForm();
        }
    }

    showSimulatedCardForm() {
        const stripeEl = document.getElementById('stripe-card-element-container');
        const simulatedEl = document.getElementById('simulated-card-form');
        if (stripeEl) stripeEl.style.display = 'none';
        if (simulatedEl) {
            simulatedEl.style.display = 'block';
            this.attachMockCardFormatting();
        }
    }

    attachMockCardFormatting() {
        const root = document.getElementById(this.containerId);
        if (!root) return;

        // Card number spaces formatting
        const cardNum = root.querySelector('.mock-card-input');
        cardNum?.addEventListener('input', (e) => {
            let val = e.target.value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
            let formatted = val.match(/.{1,4}/g)?.join(' ') || val;
            e.target.value = formatted.substring(0, 19);
        });

        // Expiry slash formatting
        const cardExpiry = root.querySelector('.mock-expiry-input');
        cardExpiry?.addEventListener('input', (e) => {
            let val = e.target.value.replace(/\//g, '').replace(/[^0-9]/gi, '');
            if (val.length >= 2) {
                e.target.value = val.substring(0, 2) + '/' + val.substring(2, 4);
            } else {
                e.target.value = val;
            }
        });

        // CVC digits lock
        const cardCvc = root.querySelector('.mock-cvc-input');
        cardCvc?.addEventListener('input', (e) => {
            e.target.value = e.target.value.replace(/[^0-9]/gi, '').substring(0, 3);
        });
    }

    // ==========================================
    // EVENT BINDINGS & USER INTERACTION
    // ==========================================
    attachEvents() {
        const root = document.getElementById(this.containerId);
        if (!root) return;

        // Back links / Go back buttons
        const backBrowse = root.querySelector('#go-back-browse-link') || root.querySelector('#go-back-browse') || root.querySelector('#wizard-cancel') || root.querySelector('#conf-go-home');
        if (backBrowse) {
            backBrowse.onclick = (e) => {
                e.preventDefault();
                this.currentView = 'browse';
                this.formStep = 1; // Reset wizard step
                this.render();
            };
        }

        const backDetail = root.querySelector('#go-back-detail-link');
        if (backDetail) {
            backDetail.onclick = (e) => {
                e.preventDefault();
                this.currentView = 'detail';
                this.render();
            };
        }

        // ------------------------------------------
        // EVENTS FOR BROWSE VIEW (VIEW 1)
        // ------------------------------------------
        if (this.currentView === 'browse') {
            root.querySelector('#fund-search')?.addEventListener('input', (e) => {
                this.searchQuery = e.target.value;
                this.render();
            });
            root.querySelector('#fund-drafts-btn')?.addEventListener('click', () => {
                this.currentView = 'drafts';
                this.render();
            });
            root.querySelector('#filter-verified-only')?.addEventListener('click', () => {
                this.filterVerifiedOnly = !this.filterVerifiedOnly;
                this.render();
            });
            root.querySelectorAll('.fg-fund-filter-chip[data-category]').forEach(btn => {
                btn.onclick = () => {
                    this.currentCategory = btn.dataset.category;
                    this.render();
                };
            });

            root.querySelector('#fund-admin-open')?.addEventListener('click', () => {
                this.currentView = 'admin';
                this.render();
            });

            // Start project button click
            root.querySelector('#start-project-btn')?.addEventListener('click', () => {
                this.currentView = 'create';
                this.formStep = 1;
                this.render();
            });

            // Project card donate button click
            root.querySelectorAll('.fg-fund-btn--card-donate').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    this.selectedProjectId = btn.dataset.id;
                    this.currentView = 'donate';
                    this.autoSelectRewardTier(25); // Set amount default to 25 and auto select tier
                    this.render();
                };
            });

            // Click on project card anywhere to view details (except the button)
            root.querySelectorAll('.fg-fund-card').forEach(card => {
                card.onclick = (e) => {
                    if (e.target.closest('.fg-fund-btn--card-donate')) return;
                    this.selectedProjectId = card.dataset.id;
                    this.currentView = 'detail';
                    this.render();
                };
            });
        }

        // ------------------------------------------
        // EVENTS FOR DETAILS VIEW (VIEW 2)
        // ------------------------------------------
        if (this.currentView === 'detail') {
            const proj = this.projects.find((p) => p.id === this.selectedProjectId);
            if (proj) recordCampaignView(proj.id);

            root.querySelectorAll('[data-detail-tab]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    this.detailTab = btn.dataset.detailTab;
                    this.render();
                });
            });

            root.querySelector('#fund-analytics-btn')?.addEventListener('click', () => {
                window.location.hash = `page=fund&project=${encodeURIComponent(this.selectedProjectId)}&view=analytics`;
                this.currentView = 'analytics';
                this.render();
            });

            root.querySelector('#detail-donate-btn')?.addEventListener('click', () => {
                this.currentView = 'donate';
                this.autoSelectRewardTier(25); // Set default amount
                this.render();
            });

            // Select reward tier directly
            root.querySelectorAll('.fg-fund-btn--reward').forEach(btn => {
                btn.onclick = () => {
                    this.selectedRewardTierId = btn.dataset.tierId;
                    this.donationAmount = Number(btn.dataset.amount);
                    this.currentView = 'donate';
                    this.render();
                };
            });

            // Detail share triggers
            const copyBtn = root.querySelector('#share-copy');
            copyBtn?.addEventListener('click', () => {
                navigator.clipboard.writeText(window.location.href).then(() => {
                    copyBtn.textContent = 'Copied! ✓';
                    setTimeout(() => { copyBtn.textContent = '🔗 Link'; }, 2000);
                });
            });

            root.querySelector('#share-twitter')?.addEventListener('click', () => {
                const text = encodeURIComponent(`Check out this women's football campaign on footballgirl.eu: ${document.title}!`);
                window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(window.location.href)}`, '_blank');
            });

            root.querySelector('#share-whatsapp')?.addEventListener('click', () => {
                const text = encodeURIComponent(`Support this campaign on footballgirl: ${window.location.href}`);
                window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
            });

            root.querySelector('#close-campaign-btn')?.addEventListener('click', async () => {
                const p = this.projects.find((x) => x.id === this.selectedProjectId);
                if (!p || !confirm('Close this campaign now? Donations will stop.')) return;
                p.status = 'closed';
                await persistCampaign(p);
                showToast(this.tr('closed'));
                this.render();
            });

            if (proj) {
                this.attachPollVoteHandlers(root, proj);
                root.querySelector('#community-comment-form')?.addEventListener('submit', (e) => {
                    e.preventDefault();
                    this.submitCommunityComment(root, proj);
                });
                root.querySelectorAll('.fg-flag-btn').forEach((btn) => {
                    btn.addEventListener('click', () => this.flagCommunityComment(proj, btn.dataset.flagId));
                });
                root.querySelectorAll('[data-delete-comment]').forEach((btn) => {
                    btn.addEventListener('click', () => this.deleteCommunityComment(proj, btn.dataset.deleteComment));
                });
            }
        }

        // ------------------------------------------
        // EVENTS FOR CREATE PROJECT WIZARD (VIEW 3)
        // ------------------------------------------
        if (this.currentView === 'create') {
            // Character count visual validation
            const shortDescInput = root.querySelector('#p-short-desc');
            const charCounter = root.querySelector('#char-count');
            shortDescInput?.addEventListener('input', (e) => {
                const len = e.target.value.length;
                if (charCounter) {
                    charCounter.textContent = `${150 - len} characters remaining`;
                    charCounter.style.color = len >= 140 ? 'red' : 'var(--text-secondary)';
                }
            });

            // Wizard Step 1 Submission
            const form1 = root.querySelector('#wizard-form-1');
            form1?.addEventListener('submit', (e) => {
                e.preventDefault();
                this.newProject.title = root.querySelector('#p-title').value.trim();
                this.newProject.category = root.querySelector('#p-category').value;
                this.newProject.shortDesc = root.querySelector('#p-short-desc').value.trim();
                this.formStep = 2;
                this.saveDraftSilent();
                this.render();
            });

            const form2 = root.querySelector('#wizard-form-2');
            root.querySelector('#wizard-prev-2')?.addEventListener('click', () => {
                this.formStep = 1;
                this.render();
            });
            if (this.formStep === 2) {
                this._getRichEditorHtml = attachRichEditor(root);
            }

            form2?.addEventListener('submit', (e) => {
                e.preventDefault();
                const html = this._getRichEditorHtml?.() || '';
                const plain = html.replace(/<[^>]+>/g, ' ').trim();
                this.newProject.bodyHtml = html;
                this.newProject.fullDesc = plain;
                if (plain.length < 30) {
                    alert('Please write at least 30 characters for your story.');
                    return;
                }
                this.formStep = 3;
                this.saveDraftSilent();
                this.render();
            });

            // Add new reward tier button logic
            root.querySelector('#add-tier-action')?.addEventListener('click', () => {
                const name = root.querySelector('#tier-name').value.trim();
                const minVal = Number(root.querySelector('#tier-min').value);
                const desc = root.querySelector('#tier-desc').value.trim();

                if (!name || minVal <= 0 || !desc) {
                    alert('Please enter a valid Reward Name, Minimum pledge, and Description.');
                    return;
                }

                const delivery = root.querySelector('#tier-delivery')?.value?.trim() || '';
                const isPrivate = !!root.querySelector('#tier-private')?.checked;
                const qty = Number(root.querySelector('#tier-quantity')?.value);
                this.newProject.rewardTiers.push({
                    id: 'tier-' + Math.random().toString(36).substring(2, 9),
                    name,
                    minDonation: minVal,
                    description: desc,
                    estimatedDelivery: delivery || null,
                    fulfilled: false,
                    isPrivate,
                    secretToken: isPrivate ? generateSecretToken() : null,
                    quantityLimit: qty > 0 ? qty : null,
                    claimed: 0,
                });

                this.render(); // Re-render to show added tier
            });

            // Remove a reward tier button logic
            root.querySelectorAll('.remove-tier-btn').forEach(btn => {
                btn.onclick = () => {
                    const idx = Number(btn.dataset.index);
                    this.newProject.rewardTiers.splice(idx, 1);
                    this.render();
                };
            });

            const form3 = root.querySelector('#wizard-form-3');
            root.querySelector('#wizard-prev-3')?.addEventListener('click', () => {
                this.formStep = 2;
                this.render();
            });
            root.querySelector('#add-faq-btn')?.addEventListener('click', () => {
                this.newProject.faqs = this.newProject.faqs || [];
                this.newProject.faqs.push({ question: '', answer: '' });
                this.render();
            });
            root.querySelector('#p-gallery-file')?.addEventListener('change', async (e) => {
                const files = [...(e.target.files || [])].slice(0, 8);
                const urls = await Promise.all(files.map((f) => new Promise((res) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result);
                    r.readAsDataURL(f);
                })));
                this.newProject.images = urls;
                this.render();
            });
            form3?.addEventListener('submit', (e) => {
                e.preventDefault();
                const target = Number(root.querySelector('#p-target').value);
                const endDateStr = root.querySelector('#p-end-date').value;
                if (target < 2000) {
                    alert('Funding target must be at least €2,000.');
                    return;
                }
                const end = new Date(endDateStr);
                const minEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                const maxEnd = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
                if (!endDateStr || end < minEnd || end > maxEnd) {
                    alert('End date must be 7–90 days from today.');
                    return;
                }
                this.newProject.target = target;
                this.newProject.endDate = endDateStr;
                this.newProject.images = this.parseGalleryInput(root.querySelector('#p-gallery')?.value);
                this.newProject.imageURL = root.querySelector('#p-image')?.value.trim() || this.newProject.images[0] || '';
                this.newProject.videoURL = root.querySelector('#p-video')?.value.trim() || '';
                this.newProject.comingSoonEnabled = !!root.querySelector('#p-coming-soon')?.checked;
                this.newProject.faqs = [...root.querySelectorAll('.fg-fund-faq-draft')].map((row) => ({
                    question: row.querySelector('.faq-q')?.value.trim(),
                    answer: row.querySelector('.faq-a')?.value.trim(),
                })).filter((f) => f.question && f.answer);
                this.formStep = 4;
                this.saveDraftSilent();
                this.render();
            });

            // Wizard Step 4 Actions & Submission
            const form4 = root.querySelector('#wizard-form-4');
            root.querySelector('#wizard-prev-4')?.addEventListener('click', () => {
                this.formStep = 3;
                this.render();
            });

            root.querySelector('#p-target')?.addEventListener('input', (e) => {
                this.newProject.target = Number(e.target.value) || 2000;
                const viz = root.querySelector('#fee-visualizer');
                if (viz && this.formStep === 4) {
                    const parent = viz.parentElement;
                    viz.outerHTML = renderFeeVisualizer(this.newProject.target, this);
                }
            });

            root.querySelector('#wizard-preview-btn')?.addEventListener('click', () => {
                this.openPreviewModal();
            });

            root.querySelector('#wizard-share-review-btn')?.addEventListener('click', async () => {
                try {
                    await this.generateReviewShareLink(true);
                    this.render();
                    showToast(this.tr('shareReviewHint'));
                } catch (e) {
                    alert(e.message);
                }
            });
            root.querySelector('#copy-review-link')?.addEventListener('click', () => {
                navigator.clipboard.writeText(this._reviewShareUrl).then(() => showToast('Link copied'));
            });
            root.querySelector('#regen-review-link')?.addEventListener('click', async () => {
                await this.generateReviewShareLink(true);
                this.render();
            });

            root.querySelector('#stripe-connect-btn')?.addEventListener('click', async () => {
                try {
                    await startStripeConnectOnboarding();
                } catch (err) {
                    alert(err.message || 'Stripe Connect failed');
                }
            });

            form4?.addEventListener('submit', (e) => {
                e.preventDefault();
                this.newProject.creatorName = root.querySelector('#p-creator').value.trim();
                this.newProject.email = root.querySelector('#p-email').value.trim();
                if (!this.newProject.title || !this.newProject.shortDesc || !this.newProject.fullDesc) {
                    alert('Please complete all project details.');
                    return;
                }
                if (!this.newProject.creatorName || !this.newProject.email) {
                    alert('Creator name and email are required.');
                    return;
                }
                this.publishCampaign();
            });
        }

        if (this.currentView === 'drafts') {
            root.querySelectorAll('[data-draft-continue]').forEach((btn) => {
                btn.onclick = () => {
                    const draft = loadDraftsForUser().find((d) => d.draftId === btn.dataset.draftContinue);
                    if (draft) {
                        this.newProject = { ...this.newProject, ...draft };
                        this.draftId = draft.draftId;
                        this.currentView = 'create';
                        this.formStep = 1;
                        this.render();
                    }
                };
            });
            root.querySelectorAll('[data-draft-delete]').forEach((btn) => {
                btn.onclick = async () => {
                    await deleteDraftById(btn.dataset.draftDelete);
                    this.render();
                };
            });
        }

        if (this.currentView === 'confirmation') {
            root.querySelector('#conf-view-campaign')?.addEventListener('click', () => {
                this.selectedProjectId = this.newProjectId;
                this.currentView = 'detail';
                this.render();
            });

            root.querySelector('#conf-go-home')?.addEventListener('click', () => {
                this.currentView = 'browse';
                this.render();
            });

            const shareUrl = root.querySelector('#conf-share-copy')?.dataset.url || window.location.href;
            root.querySelector('#conf-share-copy')?.addEventListener('click', (e) => {
                navigator.clipboard.writeText(shareUrl).then(() => {
                    e.target.textContent = 'Copied!';
                    setTimeout(() => { e.target.textContent = 'Copy link'; }, 2000);
                });
            });
            root.querySelector('#conf-share-twitter')?.addEventListener('click', () => {
                const text = encodeURIComponent('Support this women\'s football campaign on footballgirl.eu');
                window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
            });
            root.querySelector('#conf-share-whatsapp')?.addEventListener('click', () => {
                window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`, '_blank');
            });
        }

        if (this.currentView === 'donate-success') {
            root.querySelector('#success-view-project')?.addEventListener('click', () => {
                this.currentView = 'detail';
                this.resetDonationForm();
                this.render();
            });
            root.querySelector('#success-go-browse')?.addEventListener('click', () => {
                this.currentView = 'browse';
                this.resetDonationForm();
                this.render();
            });
        }

        // ------------------------------------------
        // EVENTS FOR DONATE FLOW (VIEW 4)
        // ------------------------------------------
        if (this.currentView === 'donate') {
            // Load Stripe scripts dynamically
            this.loadStripe();

            // Preset Amount Clicks
            root.querySelectorAll('.fg-fund-amount-btn').forEach(btn => {
                btn.onclick = () => {
                    const val = Number(btn.dataset.val);
                    this.donationAmount = val;
                    
                    const customInput = root.querySelector('#custom-amount-input');
                    if (customInput) customInput.value = val;

                    root.querySelectorAll('.fg-fund-amount-btn').forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');

                    this.autoSelectRewardTier(val);
                    this.updateSummaryText();
                };
            });

            // Custom Amount Input Change
            const customInput = root.querySelector('#custom-amount-input');
            customInput?.addEventListener('input', (e) => {
                const val = Number(e.target.value);
                this.donationAmount = val;
                
                // Clear preset button active states unless matching
                root.querySelectorAll('.fg-fund-amount-btn').forEach(b => {
                    b.classList.toggle('active', Number(b.dataset.val) === val);
                });

                this.autoSelectRewardTier(val);
                this.updateSummaryText();
            });

            // Donation submit action
            const donationForm = root.querySelector('#donation-submit-form');
            donationForm?.addEventListener('submit', (e) => {
                e.preventDefault();
                this.donorEmail = root.querySelector('#donor-email').value.trim();
                this.donorName = root.querySelector('#donor-name').value.trim();
                this.donorMessage = root.querySelector('#donor-message').value.trim();
                this.donationTopUp = Number(root.querySelector('#donation-topup')?.value || 0);
                this.hideDonorName = !!root.querySelector('#hide-donor-name')?.checked;
                this.donationAmount = Number(root.querySelector('#custom-amount-input')?.value || this.donationAmount);

                if (!this.donorEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.donorEmail)) {
                    alert('Please enter a valid email address.');
                    return;
                }
                if (!this.donationAmount || this.donationAmount < 5) {
                    alert('Minimum donation is €5.');
                    return;
                }
                this.autoSelectRewardTier(this.donationAmount);
                this.processDonationPayment();
            });
        }

        if (this.currentView === 'coming-soon') {
            root.querySelector('#follower-form')?.addEventListener('submit', async (e) => {
                e.preventDefault();
                const email = root.querySelector('#follower-email')?.value;
                try {
                    await addCampaignFollower(this.selectedProjectId, email);
                    showToast(this.tr('followerThanks'));
                } catch (err) {
                    alert(err.message);
                }
            });
        }

        if (this.currentView === 'analytics') {
            const project = this.projects.find((p) => p.id === this.selectedProjectId);
            if (root.querySelector('#post-update-form')) {
                attachRichEditor(root);
            }
            if (!project) return;
            root.querySelector('#post-update-form')?.addEventListener('submit', (e) => {
                e.preventDefault();
                this.postCampaignUpdateFromForm(root);
            });
            root.querySelector('#team-invite-form')?.addEventListener('submit', async (e) => {
                e.preventDefault();
                const email = root.querySelector('#collab-email')?.value;
                const role = root.querySelector('#collab-role')?.value;
                try {
                    const inv = await inviteCollaborator(project.id, {
                        email,
                        role,
                        ownerUid: getCurrentUser()?.uid,
                    });
                    const acceptUrl = `${window.location.origin}/#page=fund&project=${encodeURIComponent(project.id)}&invite=${encodeURIComponent(inv.inviteToken)}`;
                    await fetch('/api/send-collaborator-invite', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            email,
                            campaignTitle: project.title,
                            role: inv.role,
                            acceptUrl,
                        }),
                    });
                    showToast('Invite sent');
                    this.render();
                } catch (err) {
                    alert(err.message);
                }
            });
            root.querySelectorAll('[data-revoke-collab]').forEach((btn) => {
                btn.onclick = async () => {
                    await revokeCollaborator(project.id, btn.dataset.revokeCollab);
                    this.render();
                };
            });
            if (project) {
                loadCampaignClicks(project.id).then((clicks) => {
                    const section = root.querySelector('.fg-fund-analytics__section:nth-of-type(3)');
                    if (section) {
                        const h3 = section.querySelector('h3');
                        section.innerHTML = `${h3?.outerHTML || '<h3>Clicks</h3>'}${renderBarChart(aggregateClicksBySource(clicks))}`;
                    }
                });
            }
            root.querySelectorAll('[data-copy-utm]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const key = btn.dataset.copyUtm;
                    const link = this._utmLinks?.[key];
                    const full = link?.startsWith('/api/')
                        ? `${window.location.origin}${link}`
                        : link;
                    navigator.clipboard.writeText(full || '').then(() => showToast('Link copied'));
                });
            });
            root.querySelector('#fund-kyc-btn')?.addEventListener('click', async () => {
                try {
                    await startStripeIdentityVerification();
                } catch (err) {
                    alert(err.message || 'Verification failed');
                }
            });
            root.querySelector('#update-add-poll')?.addEventListener('change', (e) => {
                const fields = root.querySelector('#update-poll-fields');
                if (fields) fields.classList.toggle('hidden', !e.target.checked);
            });
            root.querySelector('#notify-on-comment')?.addEventListener('change', async (e) => {
                project.notifyOnComment = !!e.target.checked;
                await persistCampaign(project);
            });
            root.querySelectorAll('[data-mark-fulfilled]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    if (!confirm('Mark this tier as fulfilled and email backers?')) return;
                    this.markTierFulfilled(project, btn.dataset.markFulfilled);
                });
            });
            this.attachPollVoteHandlers(root, project);
            root.querySelectorAll('[data-copy-secret]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const tier = (project.rewardTiers || []).find((t) => t.secretToken === btn.dataset.copySecret);
                    if (!tier) return;
                    const url = buildSecretTierUrl(project.id, tier.secretToken);
                    navigator.clipboard.writeText(url).then(() => showToast(this.tr('copySecretLink')));
                });
            });
        }

        if (this.currentView === 'admin') {
            this.fundAdmin.attachEvents(root);
        }
    }

    autoSelectRewardTier(amount) {
        const project = this.projects.find(p => p.id === this.selectedProjectId);
        const tiers = project ? getPublicRewardTiers(project, this.secretTierToken) : [];
        if (!project || !tiers.length) return;

        // Find the highest reward tier where the minimum pledge is <= current donation amount
        let bestTier = null;
        for (const tier of tiers) {
            if (amount >= tier.minDonation) {
                if (!bestTier || tier.minDonation > bestTier.minDonation) {
                    bestTier = tier;
                }
            }
        }

        this.selectedRewardTierId = bestTier ? bestTier.id : '';

        // Update UI
        const root = document.getElementById(this.containerId);
        if (!root) return;

        const tierBanner = root.querySelector('.fg-fund-donate__tier-match');
        if (tierBanner) {
            const tiers = getPublicRewardTiers(project, this.secretTierToken);
            if (tiers.length) {
                const matched = tiers.find(t => t.id === this.selectedRewardTierId);
                tierBanner.innerHTML = matched
                    ? `<strong>Reward unlocked:</strong> ${matched.name} — ${matched.description}`
                    : '<strong>No reward tier</strong> at this amount. Increase your pledge to unlock a tier.';
            }
        }
    }

    updateSummaryText() {
        const root = document.getElementById(this.containerId);
        if (!root) return;

        const project = this.projects.find(p => p.id === this.selectedProjectId);
        if (!project) return;

        // Update Sidebar Contribution Summary Cards
        const rows = root.querySelectorAll('.fg-fund-donate__summary-row strong');
        if (rows.length >= 2) {
            rows[0].textContent = `€${this.donationAmount}`;
            rows[1].textContent = this.getRewardName(project);
        }

        const totalSpan = root.querySelector('.fg-fund-donate__summary-total span:nth-child(2)');
        if (totalSpan) totalSpan.textContent = `€${this.donationAmount}`;

        const payBtn = root.querySelector('.fg-fund-btn--pay-submit');
        if (payBtn && !this.paymentIsProcessing) {
            payBtn.textContent = 'Pay with Card';
        }

        const tierBanner = root.querySelector('.fg-fund-donate__tier-match');
        if (tierBanner) {
            const tiers = getPublicRewardTiers(project, this.secretTierToken);
            if (tiers.length) {
                const matched = tiers.find(t => t.id === this.selectedRewardTierId);
                tierBanner.innerHTML = matched
                    ? `<strong>Reward unlocked:</strong> ${matched.name} — ${matched.description}`
                    : '<strong>No reward tier</strong> at this amount. Increase your pledge to unlock a tier.';
            }
        }
    }

    async generateReviewShareLink(regenerate = false) {
        const state = this.collectFormState();
        const id = this.draftId || `draft-${Date.now()}`;
        this.draftId = id;
        const fields = regenerate || !this.newProject.previewToken
            ? generatePreviewTokenFields()
            : {
                previewToken: this.newProject.previewToken,
                previewTokenExpires: this.newProject.previewTokenExpires,
            };
        Object.assign(this.newProject, fields);
        const draft = {
            ...state,
            draftId: id,
            ...fields,
            status: 'draft',
            ownerUid: getCurrentUser()?.uid,
        };
        await upsertDraft(draft);
        const preview = normalizeProject({
            id,
            title: state.title || 'Preview',
            description: state.shortDesc,
            bodyHtml: state.bodyHtml || state.fullDesc,
            creator: state.creatorName || 'Creator',
            ...fields,
            status: 'draft',
        });
        const idx = this.projects.findIndex((p) => p.id === id);
        if (idx >= 0) this.projects[idx] = preview;
        else this.projects.push(preview);
        this._reviewShareUrl = buildPreviewShareUrl(id, fields.previewToken);
        return this._reviewShareUrl;
    }

    async postCampaignUpdateFromForm(root) {
        const project = this.projects.find((p) => p.id === this.selectedProjectId);
        if (!project || !canManageCampaign(project, getCurrentUser())) return;

        const getHtml = attachRichEditor(root);
        const bodyHtml = getHtml?.() || '';
        let poll = null;
        if (root.querySelector('#update-add-poll')?.checked) {
            const optionsRaw = (root.querySelector('#poll-options')?.value || '').split('\n');
            poll = createPollFromForm(root.querySelector('#poll-question')?.value, optionsRaw);
        }
        const update = createUpdatePayload({
            title: root.querySelector('#update-title')?.value,
            bodyHtml,
            imageUrl: root.querySelector('#update-image')?.value?.trim(),
            poll,
        });
        project.updates = [update, ...(project.updates || [])];
        await persistCampaign(project);

        const emails = getBackerEmailsFromProject(project);
        const shareUrl = getCampaignShareUrl(project.id);
        if (emails.length) {
            try {
                await fetch('/api/send-update-emails', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        emails,
                        campaignTitle: project.title,
                        updateTitle: update.title,
                        updateBodyHtml: update.bodyHtml,
                        campaignUrl: shareUrl,
                    }),
                });
            } catch (err) {
                console.warn('[Fund] update emails', err);
            }
        }
        showToast(`Update published${emails.length ? ` — emailed ${emails.length} backers` : ''}`);
        this.currentView = 'detail';
        this.detailTab = 'updates';
        this.render();
    }

    async submitCommunityComment(root, project) {
        const body = root.querySelector('#community-comment-body')?.value?.trim();
        if (!body) return;
        const comment = createCommentPayload(body);
        project.communityComments = [comment, ...(project.communityComments || [])];
        await persistCampaign(project);
        if (project.notifyOnComment !== false && project.email) {
            try {
                await fetch('/api/send-comment-notify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        creatorEmail: project.email,
                        campaignTitle: project.title,
                        commenterName: comment.userName,
                        body: comment.body,
                    }),
                });
            } catch (err) {
                console.warn('[Fund] comment notify', err);
            }
        }
        showToast(this.tr('postComment'));
        this.render();
    }

    async flagCommunityComment(project, commentId) {
        const list = project.communityComments || [];
        const c = list.find((x) => x.id === commentId);
        if (!c) return;
        c.flags = (c.flags || 0) + 1;
        if (c.flags >= 3) c.hidden = true;
        await persistCampaign(project);
        showToast('Comment flagged');
        this.render();
    }

    async deleteCommunityComment(project, commentId) {
        project.communityComments = (project.communityComments || []).filter((c) => c.id !== commentId);
        await persistCampaign(project);
        this.render();
    }

    async voteOnPoll(root, project, updateId, optionIndex) {
        const email = root.querySelector('.fg-poll-email')?.value?.trim()
            || getCurrentUser()?.email;
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert('Enter your email to vote.');
            return;
        }
        if (hasVoted(project.id, updateId, email)) {
            showToast('You already voted on this poll');
            return;
        }
        try {
            await fetch('/api/vote-poll', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    campaignId: project.id,
                    updateId,
                    optionIndex,
                    email,
                }),
            });
        } catch (err) {
            console.warn('[Fund] vote-poll', err);
        }
        recordLocalVote(project.id, updateId, email, optionIndex);
        showToast(this.tr('votePoll'));
        this.detailTab = 'updates';
        this.render();
    }

    async markTierFulfilled(project, tierId) {
        const tier = (project.rewardTiers || []).find((t) => t.id === tierId);
        if (!tier || tier.fulfilled) return;
        tier.fulfilled = true;
        await persistCampaign(project);
        const emails = [...new Set(getBackersForTier(project, tierId).map((b) => b.donorEmail).filter(Boolean))];
        if (emails.length) {
            try {
                await fetch('/api/send-tier-fulfilled', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        emails,
                        campaignTitle: project.title,
                        tierName: tier.name,
                    }),
                });
            } catch (err) {
                console.warn('[Fund] tier fulfilled emails', err);
            }
        }
        showToast(`${this.tr('tierFulfilled')}${emails.length ? ` — ${emails.length} emailed` : ''}`);
        this.render();
    }

    attachPollVoteHandlers(root, project) {
        root.querySelectorAll('.fg-fund-poll__opt').forEach((btn) => {
            btn.addEventListener('click', () => {
                const updateId = btn.closest('.fg-fund-poll')?.dataset?.updateId
                    || btn.dataset.pollUpdate;
                const optionIndex = Number(btn.dataset.pollOpt);
                this.voteOnPoll(root, project, updateId, optionIndex);
            });
        });
    }

    openPreviewModal() {
        const state = this.collectFormState();
        const tempId = 'preview-draft';
        const preview = normalizeProject({
            id: tempId,
            title: state.title || 'Preview',
            category: state.category,
            target: state.target || 2000,
            raised: 0,
            endDate: state.endDate || new Date(Date.now() + 30 * 86400000).toISOString(),
            description: state.shortDesc,
            fullStory: state.fullDesc,
            creator: state.creatorName || 'You',
            images: state.images,
            imageURL: state.imageURL,
            videoURL: state.videoURL,
            rewardTiers: state.rewardTiers,
            faqs: state.faqs,
            status: 'draft',
        });
        const existing = this.projects.find((p) => p.id === tempId);
        if (existing) Object.assign(existing, preview);
        else this.projects.push(preview);
        this.selectedProjectId = tempId;
        this.previewMode = true;
        this.currentView = 'detail';
        this.render();
    }

    async publishCampaign() {
        const existingIds = new Set(this.projects.map((p) => p.id));
        const uniqueId = uniqueCampaignId(this.newProject.creatorName, this.newProject.title, existingIds);
        const slug = slugify(this.newProject.creatorName, this.newProject.title);
        const randomGradients = [
            "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
            "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            "linear-gradient(135deg, #7028e4 0%, #e5e5be 100%)"
        ];
        const randomGrad = randomGradients[Math.floor(Math.random() * randomGradients.length)];

        const images = this.newProject.images?.length
            ? this.newProject.images
            : (this.newProject.imageURL ? [this.newProject.imageURL] : []);

        if (!this.canPublishCampaign()) {
            showToast(this.tr('publishRequiresStripe'), 'error');
            return;
        }

        const stripeVerified = !!this.creatorStripe?.stripeVerified;
        const campaign = normalizeProject({
            id: uniqueId,
            slug,
            status: 'live',
            currency: 'eur',
            financialLocked: stripeVerified,
            stripeAccountId: this.creatorStripe?.stripeAccountId || null,
            stripeVerified,
            kycVerified: !!this.creatorKyc?.kycVerified,
            title: this.newProject.title,
            category: this.newProject.category,
            target: this.newProject.target,
            raised: 0,
            endDate: new Date(this.newProject.endDate).toISOString(),
            description: this.newProject.shortDesc,
            fullStory: this.newProject.fullDesc,
            creator: this.newProject.creatorName,
            imageGradient: randomGrad,
            imageURL: images[0] || '',
            images,
            videoURL: this.newProject.videoURL,
            email: this.newProject.email,
            ownerUid: getCurrentUser()?.uid || null,
            donorsCount: 0,
            createdAt: Date.now(),
            updates: [],
            comments: [],
            communityComments: [],
            notifyOnComment: true,
            rewardTiers: this.newProject.rewardTiers,
            faqs: this.newProject.faqs || [],
            comingSoonEnabled: !!this.newProject.comingSoonEnabled,
            bodyHtml: this.newProject.bodyHtml || this.newProject.fullDesc,
        });

        await persistCampaign(campaign);
        this.projects = this.projects.filter((p) => p.id !== 'preview-draft');
        const idx = this.projects.findIndex((p) => p.id === uniqueId);
        if (idx >= 0) this.projects[idx] = campaign;
        else this.projects.push(campaign);

        if (this.draftId) mergeFollowerLists(this.draftId, uniqueId);

        const shareUrl = getCampaignShareUrl(uniqueId);
        if (this.newProject.comingSoonEnabled) {
            const teaserUrl = `${getCampaignShareUrl(uniqueId)}&view=coming-soon`;
            showToast(`Coming Soon link: ${teaserUrl}`);
        }

        try {
            const followers = await getCampaignFollowers(uniqueId);
            if (followers.length) {
                await fetch('/api/send-follower-emails', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        emails: followers,
                        campaignTitle: campaign.title,
                        campaignUrl: shareUrl,
                    }),
                });
            }
        } catch (err) {
            console.warn('[Fund] follower emails failed', err);
        }

        try {
            await fetch('/api/send-campaign-live', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    creatorEmail: campaign.email,
                    creatorName: campaign.creator,
                    campaignTitle: campaign.title,
                    campaignUrl: shareUrl,
                }),
            });
        } catch (err) {
            console.warn('[Fund] live email failed', err);
        }

        if (this.draftId) await deleteDraftById(this.draftId);

        this.newProjectId = uniqueId;
        this.selectedProjectId = uniqueId;
        this.previewMode = false;
        this.currentView = 'confirmation';
        this.formStep = 1;
        this.resetNewProjectForm();
        showToast(this.tr('campaignLiveToast'));
        window.location.hash = `page=fund&project=${encodeURIComponent(uniqueId)}`;
        this.render();
    }

    resetNewProjectForm() {
        this.newProject = {
            title: '',
            category: 'Clubs',
            shortDesc: '',
            fullDesc: '',
            bodyHtml: '',
            target: 2000,
            endDate: '',
            rewardTiers: [],
            imageURL: '',
            images: [],
            videoURL: '',
            creatorName: '',
            email: '',
            faqs: [],
            comingSoonEnabled: false,
        };
    }

    // ==========================================
    // DONATION SUBMIT WITH STRIPE/SIMULATION
    // ==========================================
    async processDonationPayment() {
        const root = document.getElementById(this.containerId);
        if (!root) return;

        const project = this.projects.find((p) => p.id === this.selectedProjectId);

        this.paymentIsProcessing = true;
        this.renderProcessingState();

        const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
        const totalEur = this.donationAmount + (this.donationTopUp || 0);
        const amountCents = Math.round(totalEur * 100);

        if (!stripeKey || !window.Stripe) {
            this.completeSimulatedDonation();
            return;
        }

        if (!this.stripeInstance || !this.stripeCard) {
            this.initStripeElements();
        }

        const usingMockCard = document.getElementById('simulated-card-form')?.style.display === 'block';
        if (usingMockCard) {
            this.completeSimulatedDonation();
            return;
        }

        try {
            const response = await fetch('/api/create-payment-intent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: amountCents,
                    currency: project?.currency || 'eur',
                    projectId: this.selectedProjectId,
                    donorEmail: this.donorEmail,
                    destinationAccountId: project?.stripeAccountId || null,
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || `Payment setup failed (${response.status})`);
            }
            if (!data.clientSecret) {
                throw new Error('Missing clientSecret from payment server');
            }

            const stripeEl = document.getElementById('stripe-card-element-container');
            const simulatedEl = document.getElementById('simulated-card-form');
            if (stripeEl) stripeEl.style.display = '';
            if (simulatedEl) simulatedEl.style.display = 'none';

            if (!this.stripeCard) {
                this.initStripeElements();
            }

            const { error, paymentIntent } = await this.stripeInstance.confirmCardPayment(
                data.clientSecret,
                {
                    payment_method: {
                        card: this.stripeCard,
                        billing_details: {
                            email: this.donorEmail,
                            name: this.donorName || undefined,
                        },
                    },
                }
            );

            if (error) {
                this.paymentIsProcessing = false;
                const errEl = document.getElementById('stripe-errors');
                if (errEl) errEl.textContent = error.message;
                const payBtn = root.querySelector('.fg-fund-btn--pay-submit');
                if (payBtn) {
                    payBtn.disabled = false;
                    payBtn.textContent = 'Pay with Card';
                }
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                await this.completeSuccessfulDonation();
                return;
            }

            throw new Error(`Payment status: ${paymentIntent?.status || 'unknown'}`);
        } catch (err) {
            console.warn('[Fund] Payment API failed, falling back to simulation:', err.message);
            this.showSimulatedCardForm();
            this.completeSimulatedDonation();
        }
    }

    completeSimulatedDonation() {
        setTimeout(() => {
            this.completeSuccessfulDonation();
        }, 1500);
    }

    renderProcessingState() {
        const root = document.getElementById(this.containerId);
        if (!root) return;

        const payBtn = root.querySelector('.fg-fund-btn--pay-submit');
        if (payBtn) {
            payBtn.disabled = true;
            payBtn.innerHTML = '<span class="fg-fund-spinner"></span> Securely processing...';
        }
    }

    async completeSuccessfulDonation() {
        this.paymentIsProcessing = false;

        let project = null;
        const projectIndex = this.projects.findIndex(p => p.id === this.selectedProjectId);
        if (projectIndex !== -1) {
            project = this.projects[projectIndex];
            const totalEur = this.donationAmount + (this.donationTopUp || 0);
            project.raised += totalEur;
            project.donorsCount = (project.donorsCount || 0) + 1;

            const dName = this.hideDonorName ? 'Anonymous' : (this.donorName.trim() || 'Anonymous');
            if (this.donorMessage.trim() || totalEur > 0) {
                project.comments = project.comments || [];
                project.comments.unshift({
                    donorName: dName,
                    donorEmail: this.donorEmail,
                    message: this.donorMessage.trim() || `Supported the project with €${totalEur}!`,
                    amount: totalEur,
                    tierAmount: this.donationAmount,
                    topUpAmount: this.donationTopUp || 0,
                    rewardTierId: this.selectedRewardTierId || null,
                    hideName: this.hideDonorName,
                    timestamp: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                });
            }

            this.projects[projectIndex] = project;
            await persistCampaign(project);
        }

        await this.sendDonationEmails(project);

        this.currentView = 'donate-success';
        this.render();
    }

    async sendDonationEmails(project) {
        if (!project || !this.donorEmail) return;

        try {
            await fetch('/api/send-donation-emails', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    donorEmail: this.donorEmail,
                    donorName: this.donorName,
                    amount: Math.round(this.donationAmount * 100),
                    projectTitle: project.title,
                    projectCreator: project.creator,
                    creatorEmail: project.email || '',
                    donorMessage: this.donorMessage,
                    projectId: project.id,
                }),
            });
        } catch (err) {
            console.warn('[Fund] Email notifications failed:', err.message);
        }
    }

    resetDonationForm() {
        this.donationAmount = 25;
        this.donorName = '';
        this.donorEmail = '';
        this.donorMessage = '';
        this.selectedRewardTierId = '';
        this.stripeInstance = null;
        this.stripeElements = null;
        this.stripeCard = null;
        this.paymentIsProcessing = false;
    }

    injectStyles() {
        const styleId = 'fg-fund-styles';
        if (document.getElementById(styleId)) return;

        const css = `
            .animate-fade-in {
                animation: fgFadeIn 0.3s ease-in-out;
            }
            
            @keyframes fgFadeIn {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }

            .fg-fund-loading {
                padding: 100px;
                text-align: center;
                font-weight: 700;
                font-size: 1.25rem;
                color: var(--text-secondary);
            }

            .fg-fund-error {
                padding: 48px;
                text-align: center;
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                border-radius: 12px;
                color: var(--text-color);
            }

            .fg-fund-empty {
                grid-column: 1 / -1;
                text-align: center;
                padding: 60px 20px;
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                border-radius: 16px;
            }

            .fg-fund-empty h3 {
                font-size: 1.5rem;
                font-weight: 800;
                margin-bottom: 8px;
                color: var(--text-color);
            }

            .fg-fund-empty p, .fg-fund-empty-sub {
                color: var(--text-secondary);
            }

            /* Hero Banner */
            .fg-fund-hero {
                background: linear-gradient(135deg, #E91E8C 0%, #ff52ad 100%);
                border-radius: 16px;
                padding: 48px;
                margin-bottom: 32px;
                color: #ffffff;
                box-shadow: 0 10px 30px rgba(233, 30, 140, 0.15);
            }

            .fg-fund-hero h1 {
                font-size: 2.5rem;
                font-weight: 900;
                margin: 0 0 12px;
                line-height: 1.1;
                letter-spacing: -0.5px;
            }

            .fg-fund-hero p {
                font-size: 1.1rem;
                opacity: 0.9;
                max-width: 600px;
                margin: 0 0 28px;
                line-height: 1.5;
            }

            /* Buttons */
            .fg-fund-btn {
                display: inline-block;
                width: 100%;
                text-align: center;
                font-family: inherit;
                font-weight: 800;
                border-radius: 99px;
                border: none;
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            }

            .fg-fund-btn--hero-start {
                width: auto;
                padding: 14px 32px;
                background: #ffffff;
                color: #E91E8C;
                font-size: 1rem;
                box-shadow: 0 4px 14px rgba(255, 255, 255, 0.25);
            }

            .fg-fund-btn--hero-start:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(255, 255, 255, 0.4);
                background: #f8f9fa;
            }

            .fg-fund-btn--card-donate {
                width: auto;
                padding: 10px 20px;
                background: #E91E8C;
                color: #ffffff;
                font-size: 0.9rem;
            }

            .fg-fund-btn--card-donate:hover {
                background: #d81b7a;
                transform: translateY(-1px);
            }

            .fg-fund-btn--primary {
                background: #E91E8C;
                color: #ffffff;
            }

            .fg-fund-btn--primary:hover {
                background: #d81b7a;
            }

            .fg-fund-btn--danger-text {
                width: auto;
                background: transparent;
                color: #ef4444;
                font-size: 0.85rem;
                font-weight: 700;
            }

            .fg-fund-btn--danger-text:hover {
                text-decoration: underline;
            }

            /* Back Links */
            .fg-fund-back-link {
                background: none;
                border: none;
                color: #E91E8C;
                font-weight: 700;
                cursor: pointer;
                padding: 0;
                margin-bottom: 20px;
                display: block;
                font-size: 0.95rem;
                font-family: inherit;
            }

            .fg-fund-back-link:hover {
                text-decoration: underline;
            }

            /* Filters */
            .fg-fund-filters-container {
                margin-bottom: 24px;
                position: relative;
            }

            .fg-fund-filters {
                display: flex;
                gap: 10px;
                overflow-x: auto;
                scrollbar-width: none;
                -ms-overflow-style: none;
                padding: 4px 0;
            }

            .fg-fund-filters::-webkit-scrollbar {
                display: none;
            }

            .fg-fund-filter-chip {
                background: var(--card-bg, #ffffff);
                border: 1px solid var(--border-color, #e5e5e5);
                color: var(--text-secondary, #666666);
                padding: 8px 18px;
                border-radius: 99px;
                font-size: 0.85rem;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.2s;
                white-space: nowrap;
            }

            .fg-fund-filter-chip:hover {
                background: var(--hover-bg, #f5f6f8);
                color: var(--text-color, #333333);
            }

            .fg-fund-filter-chip.active {
                background: #E91E8C;
                border-color: #E91E8C;
                color: #ffffff;
            }

            /* Card Grid */
            .fg-fund-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 28px;
            }

            .fg-fund-card {
                background: var(--card-bg, #ffffff);
                border: 1px solid var(--border-color, #e5e5e5);
                border-radius: 16px;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
                display: flex;
                flex-direction: column;
                cursor: pointer;
                transition: transform 0.25s, box-shadow 0.25s;
            }

            .fg-fund-card:hover {
                transform: translateY(-5px);
                box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
            }

            .fg-fund-card__image {
                height: 180px;
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
            }

            .fg-fund-card__body {
                padding: 20px;
                flex: 1;
                display: flex;
                flex-direction: column;
            }

            .fg-fund-card__category {
                font-size: 0.75rem;
                font-weight: 800;
                text-transform: uppercase;
                color: #E91E8C;
                letter-spacing: 0.5px;
                margin-bottom: 8px;
                display: inline-block;
            }

            .fg-fund-card__title {
                font-size: 1.15rem;
                font-weight: 800;
                line-height: 1.35;
                color: var(--text-color, #333333);
                margin: 0 0 4px;
            }

            .fg-fund-card__creator {
                font-size: 0.8rem;
                color: var(--text-secondary, #666666);
                margin: 0 0 14px;
            }

            .fg-fund-card__desc {
                font-size: 0.9rem;
                line-height: 1.5;
                color: var(--text-secondary, #666666);
                margin: 0 0 20px;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                flex: 1;
            }

            /* Progress Bar */
            .fg-fund-card__progress, .fg-fund-detail__progress {
                margin-bottom: 20px;
            }

            .fg-fund-card__progress-bar, .fg-fund-detail__progress-bar {
                height: 6px;
                background: var(--hover-bg, #f5f6f8);
                border-radius: 99px;
                overflow: hidden;
                margin-bottom: 6px;
            }

            .fg-fund-detail__progress-bar {
                height: 10px;
            }

            .fg-fund-card__progress-fill, .fg-fund-detail__progress-fill {
                height: 100%;
                background: #E91E8C;
                border-radius: 99px;
            }

            .fg-fund-card__progress-info {
                display: flex;
                justify-content: space-between;
                font-size: 0.8rem;
            }

            .fg-fund-card__percentage {
                font-weight: 800;
                color: var(--text-color, #333333);
            }

            .fg-fund-card__raised {
                color: var(--text-secondary, #666666);
            }

            /* Card Footer */
            .fg-fund-card__footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-top: 1px solid var(--border-color, #e5e5e5);
                padding-top: 14px;
            }

            .fg-fund-card__days {
                display: flex;
                flex-direction: column;
            }

            .fg-fund-card__days-number {
                font-size: 1.15rem;
                font-weight: 900;
                color: var(--text-color, #333333);
                line-height: 1;
            }

            .fg-fund-card__days-label {
                font-size: 0.7rem;
                color: var(--text-secondary, #666666);
                font-weight: 700;
                text-transform: uppercase;
            }

            /* ==========================================
               VIEW 2: DETAIL VIEW STYLES
               ========================================== */
            .fg-fund-detail__hero {
                border-radius: 16px;
                background-size: cover;
                background-position: center;
                margin-bottom: 32px;
                overflow: hidden;
                min-height: 280px;
            }

            .fg-fund-detail__hero-overlay {
                background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.2) 100%);
                padding: 32px;
                color: #fff;
                min-height: 280px;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
            }

            .fg-fund-detail__hero-title {
                font-size: 2rem;
                font-weight: 900;
                margin: 8px 0;
                line-height: 1.15;
            }

            .fg-fund-detail__hero-creator {
                margin: 0 0 16px;
                opacity: 0.9;
            }

            .fg-fund-detail__progress--hero {
                margin-bottom: 16px;
            }

            .fg-fund-detail__progress--hero .fg-fund-detail__progress-bar {
                height: 10px;
            }

            .fg-fund-detail__stats-row {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
            }

            .fg-fund-detail__stat-pill {
                background: rgba(255,255,255,0.15);
                border-radius: 8px;
                padding: 8px 14px;
                font-size: 0.85rem;
            }

            .fg-fund-detail__stat-pill strong {
                display: block;
                font-size: 1.1rem;
            }

            .fg-fund-detail__stat-pill span {
                opacity: 0.85;
                font-size: 0.7rem;
                text-transform: uppercase;
                font-weight: 700;
            }

            .fg-fund-detail__video {
                margin-bottom: 24px;
                border-radius: 12px;
                overflow: hidden;
                aspect-ratio: 16/9;
            }

            .fg-fund-detail__video iframe {
                width: 100%;
                height: 100%;
                border: 0;
            }

            .fg-fund-detail__title-section {
                margin-bottom: 30px;
            }

            .fg-fund-detail__tag {
                display: inline-block;
                background: #E91E8C;
                color: #ffffff;
                font-size: 0.75rem;
                font-weight: 800;
                text-transform: uppercase;
                padding: 4px 12px;
                border-radius: 99px;
                margin-bottom: 12px;
            }

            .fg-fund-detail__title-section h1 {
                font-size: 2.25rem;
                font-weight: 900;
                line-height: 1.15;
                color: var(--text-color);
                margin: 0 0 6px;
            }

            .fg-fund-detail__title-section p {
                margin: 0;
                color: var(--text-secondary);
                font-size: 1rem;
            }

            .fg-fund-detail__layout {
                display: grid;
                grid-template-columns: 65% 35%;
                gap: 40px;
            }

            .fg-fund-detail__hero-image {
                height: 380px;
                border-radius: 16px;
                background-size: cover;
                background-position: center;
                margin-bottom: 32px;
                box-shadow: var(--shadow-md);
            }

            .fg-fund-detail__description {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                padding: 32px;
                border-radius: 16px;
                margin-bottom: 32px;
            }

            .fg-fund-detail__description h3, .fg-fund-detail__section h3 {
                font-size: 1.35rem;
                font-weight: 800;
                margin: 0 0 16px;
                color: var(--text-color);
            }

            .fg-fund-detail__description p {
                font-size: 1.05rem;
                line-height: 1.65;
                color: var(--text-secondary);
                margin: 0 0 20px;
            }

            .fg-fund-detail__description p:last-child {
                margin-bottom: 0;
            }

            .fg-fund-detail__section {
                border-top: 1px solid var(--border-color);
                padding-top: 32px;
                margin-bottom: 32px;
            }

            /* Updates Feed */
            .fg-fund-update-item {
                border-left: 3px solid #E91E8C;
                padding-left: 20px;
                margin-bottom: 24px;
            }

            .fg-fund-update-item__date {
                font-weight: 800;
                font-size: 0.85rem;
                color: #E91E8C;
                text-transform: uppercase;
                margin-bottom: 4px;
            }

            .fg-fund-update-item__text {
                font-size: 1rem;
                line-height: 1.5;
                color: var(--text-color);
            }

            /* Comments Feed */
            .fg-fund-comment-item {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                padding: 20px;
                border-radius: 12px;
                margin-bottom: 16px;
            }

            .fg-fund-comment-item__header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
            }

            .fg-fund-comment-item__author {
                font-weight: 800;
                color: var(--text-color);
            }

            .fg-fund-comment-item__amount {
                color: #E91E8C;
                font-weight: 700;
                font-size: 0.85rem;
            }

            .fg-fund-comment-item__message {
                margin: 0 0 8px;
                font-size: 0.95rem;
                line-height: 1.45;
                color: var(--text-secondary);
            }

            .fg-fund-comment-item__date {
                font-size: 0.75rem;
                color: var(--text-secondary);
            }

            /* Stats Card */
            .fg-fund-detail__stats-card {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                border-radius: 16px;
                padding: 28px;
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
                margin-bottom: 28px;
                position: sticky;
                top: 20px;
            }

            .fg-fund-detail__stat {
                border-bottom: 1px solid var(--border-color);
                padding: 16px 0;
            }

            .fg-fund-detail__stat:first-child {
                padding-top: 0;
            }

            .fg-fund-detail__stat-number {
                display: block;
                font-size: 2.25rem;
                font-weight: 900;
                color: var(--text-color);
                line-height: 1.1;
                letter-spacing: -0.5px;
            }

            .fg-fund-detail__stat-label {
                font-size: 0.85rem;
                color: var(--text-secondary);
                font-weight: 600;
            }

            .fg-fund-detail__pct {
                font-size: 0.85rem;
                font-weight: 800;
                color: var(--text-color);
                display: block;
                text-align: right;
            }

            .fg-fund-btn--donate {
                margin-top: 24px;
                padding: 16px 24px;
                font-size: 1.05rem;
            }

            /* Sharing Panel */
            .fg-fund-detail__share-container {
                margin-top: 28px;
                border-top: 1px solid var(--border-color);
                padding-top: 20px;
            }

            .fg-fund-detail__share-title {
                display: block;
                font-size: 0.8rem;
                font-weight: 800;
                color: var(--text-secondary);
                text-transform: uppercase;
                margin-bottom: 10px;
            }

            .fg-fund-detail__share-buttons {
                display: flex;
                gap: 10px;
            }

            .fg-fund-share-btn {
                flex: 1;
                padding: 8px 12px;
                font-family: inherit;
                font-weight: 700;
                font-size: 0.85rem;
                border-radius: 8px;
                border: 1px solid var(--border-color);
                background: var(--hover-bg);
                color: var(--text-color);
                cursor: pointer;
                transition: 0.2s;
            }

            .fg-fund-share-btn:hover {
                filter: brightness(0.95);
            }

            /* Reward Cards */
            .fg-fund-reward-card {
                background: var(--card-bg);
                border: 2px solid var(--border-color);
                padding: 24px;
                border-radius: 12px;
                margin-bottom: 16px;
                transition: border-color 0.25s;
            }

            .fg-fund-reward-card:hover {
                border-color: #E91E8C;
            }

            .fg-fund-reward-card__min {
                display: block;
                font-size: 0.85rem;
                font-weight: 800;
                color: #E91E8C;
                margin-bottom: 4px;
            }

            .fg-fund-reward-card__name {
                font-size: 1.1rem;
                font-weight: 800;
                margin: 0 0 10px;
                color: var(--text-color);
            }

            .fg-fund-reward-card__desc {
                font-size: 0.9rem;
                line-height: 1.45;
                color: var(--text-secondary);
                margin: 0 0 20px;
            }

            .fg-fund-btn--reward {
                background: var(--hover-bg);
                color: var(--text-color);
                border: 1px solid var(--border-color);
                padding: 10px 16px;
                font-size: 0.85rem;
            }

            .fg-fund-btn--reward:hover {
                background: #E91E8C;
                color: #ffffff;
                border-color: #E91E8C;
            }

            /* ==========================================
               VIEW 3: CREATE + CONFIRMATION
               ========================================== */
            .fg-fund-create,
            .fg-fund-wizard {
                max-width: 680px;
                margin: 0 auto;
                text-align: left;
            }

            .fg-fund-create__header,
            .fg-fund-wizard__header {
                margin-bottom: 32px;
            }

            .fg-fund-wizard__header h2 {
                font-size: 2rem;
                font-weight: 900;
                color: var(--text-color);
                margin: 0 0 6px;
            }

            .fg-fund-wizard__header p {
                color: var(--text-secondary);
                margin: 0;
            }

            .fg-fund-wizard__stepper {
                display: flex;
                align-items: center;
                margin-bottom: 40px;
            }

            .fg-fund-wizard__step {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 0.9rem;
                font-weight: 700;
                color: var(--text-secondary);
            }

            .fg-fund-wizard__step span {
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: var(--hover-bg);
                color: var(--text-secondary);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.8rem;
                font-weight: 800;
            }

            .fg-fund-wizard__step.active {
                color: #E91E8C;
            }

            .fg-fund-wizard__step.active span {
                background: #E91E8C;
                color: #ffffff;
            }

            .fg-fund-wizard__step-line {
                flex: 1;
                height: 2px;
                background: var(--border-color);
                margin: 0 16px;
            }

            .fg-fund-wizard__step-line.active {
                background: #E91E8C;
            }

            .fg-fund-wizard__actions {
                display: flex;
                gap: 16px;
                margin-top: 32px;
                border-top: 1px solid var(--border-color);
                padding-top: 24px;
            }

            .fg-fund-wizard__actions button {
                flex: 1;
                padding: 14px 20px;
            }

            .fg-fund-wizard__added-tier {
                padding: 12px 16px;
                background: var(--hover-bg);
                border: 1px solid var(--border-color);
                border-radius: 8px;
                margin-bottom: 10px;
            }

            .fg-fund-wizard__details-notice {
                padding: 16px;
                background: #fdf2f8;
                border: 1px solid #fbcfe8;
                border-radius: 10px;
                color: #be185d;
                font-size: 0.9rem;
                line-height: 1.5;
                margin-top: 24px;
            }

            .fg-fund-wizard__details-notice strong {
                display: block;
                margin-bottom: 6px;
            }

            /* Step 5 confirmation */
            .fg-fund-wizard__confirmation {
                text-align: center;
                padding: 40px 20px;
            }

            .fg-fund-wizard__conf-icon {
                font-size: 4rem;
                margin-bottom: 20px;
                animation: fgConfPulse 1s ease-in-out infinite alternate;
            }

            @keyframes fgConfPulse {
                from { transform: scale(1); }
                to { transform: scale(1.1); }
            }

            .fg-fund-wizard__confirmation h3 {
                font-size: 1.75rem;
                font-weight: 900;
                margin: 0 0 10px;
                color: var(--text-color);
            }

            .fg-fund-wizard__confirmation p {
                color: var(--text-secondary);
                max-width: 450px;
                margin: 0 auto 32px;
            }

            .fg-fund-wizard__conf-links {
                display: flex;
                gap: 16px;
                max-width: 400px;
                margin: 0 auto 32px;
            }

            .fg-fund-wizard__conf-links button {
                padding: 14px 20px;
            }

            .fg-fund-wizard__conf-share {
                border-top: 1px solid var(--border-color);
                padding-top: 24px;
                display: inline-block;
            }

            .fg-fund-wizard__conf-share span {
                font-size: 0.85rem;
                font-weight: 800;
                color: var(--text-secondary);
                text-transform: uppercase;
            }

            /* ==========================================
               VIEW 4: DONATE FLOW STYLES
               ========================================== */
            .fg-fund-donate {
                max-width: 900px;
                margin: 0 auto;
                text-align: left;
            }

            .fg-fund-donate__header {
                margin-bottom: 32px;
            }

            .fg-fund-donate__header h2 {
                font-size: 2rem;
                font-weight: 900;
                color: var(--text-color);
                margin: 0 0 6px;
            }

            .fg-fund-donate__header p {
                color: var(--text-secondary);
                margin: 0;
            }

            .fg-fund-donate__layout {
                display: grid;
                grid-template-columns: 60% 40%;
                gap: 40px;
            }

            .fg-fund-donate__amount-box, .fg-fund-donate__rewards-picker, .fg-fund-donate__info-section, .fg-fund-donate__stripe-box {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                padding: 24px;
                border-radius: 12px;
                margin-bottom: 24px;
            }

            .fg-fund-donate__amount-box h3, .fg-fund-donate__rewards-picker h3, .fg-fund-donate__info-section h3, .fg-fund-donate__stripe-box h3 {
                font-size: 1.2rem;
                font-weight: 800;
                margin: 0 0 16px;
                color: var(--text-color);
            }

            .fg-fund-donate__amount-presets {
                display: grid;
                grid-template-columns: repeat(5, 1fr);
                gap: 10px;
            }

            .fg-fund-amount-btn {
                padding: 12px;
                background: var(--hover-bg);
                border: 1.5px solid var(--border-color);
                color: var(--text-color);
                font-weight: 800;
                font-size: 0.95rem;
                border-radius: 8px;
                cursor: pointer;
                font-family: inherit;
                transition: all 0.2s;
            }

            .fg-fund-amount-btn:hover {
                border-color: #E91E8C;
                color: #E91E8C;
            }

            .fg-fund-amount-btn.active {
                background: #E91E8C;
                border-color: #E91E8C;
                color: #ffffff;
            }

            .fg-fund-donate-tier-opt {
                display: flex;
                gap: 12px;
                padding: 16px;
                border: 1.5px solid var(--border-color);
                border-radius: 8px;
                margin-bottom: 12px;
                cursor: pointer;
                transition: border-color 0.25s;
            }

            .fg-fund-donate-tier-opt:hover {
                border-color: #E91E8C;
            }

            .fg-fund-donate-tier-opt.selected {
                border-color: #E91E8C;
                background: #fdf2f8;
            }

            [data-theme="dark"] .fg-fund-donate-tier-opt.selected {
                background: #25121c;
            }

            .fg-fund-donate-tier-opt input {
                margin-top: 4px;
            }

            .fg-fund-donate-tier-opt__name {
                font-weight: 800;
                font-size: 0.95rem;
                color: var(--text-color);
                display: block;
                margin-bottom: 2px;
            }

            .fg-fund-donate-tier-opt__desc {
                margin: 0;
                font-size: 0.85rem;
                color: var(--text-secondary);
                line-height: 1.4;
            }

            .fg-fund-btn--pay-submit {
                padding: 16px;
                font-size: 1.05rem;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
            }

            /* Summary Card */
            .fg-fund-donate__summary-card {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                padding: 24px;
                border-radius: 12px;
            }

            .fg-fund-donate__summary-card h3 {
                font-size: 1.15rem;
                font-weight: 800;
                margin: 0 0 16px;
                border-bottom: 1px solid var(--border-color);
                padding-bottom: 12px;
                color: var(--text-color);
            }

            .fg-fund-donate__summary-row {
                display: flex;
                justify-content: space-between;
                font-size: 0.9rem;
                padding-bottom: 12px;
                margin-bottom: 12px;
                border-bottom: 1px dashed var(--border-color);
            }

            .fg-fund-donate__summary-row span {
                color: var(--text-secondary);
            }

            .fg-fund-donate__summary-row strong {
                color: var(--text-color);
                max-width: 150px;
                text-align: right;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .fg-fund-donate__summary-total {
                display: flex;
                justify-content: space-between;
                font-size: 1.1rem;
                font-weight: 800;
                margin-top: 16px;
                color: var(--text-color);
            }

            /* Loader / Spinner */
            .fg-fund-spinner {
                width: 20px;
                height: 20px;
                border: 3px solid rgba(255, 255, 255, 0.3);
                border-radius: 50%;
                border-top-color: #ffffff;
                animation: fgSpin 0.8s linear infinite;
                display: inline-block;
            }

            @keyframes fgSpin {
                to { transform: rotate(360deg); }
            }

            /* Success screen */
            .fg-fund-success-screen {
                text-align: center;
                max-width: 580px;
                margin: 0 auto;
                padding: 40px 20px;
            }

            .fg-fund-success-screen__icon {
                font-size: 4.5rem;
                margin-bottom: 24px;
                animation: fgConfPulse 1s ease-in-out infinite alternate;
            }

            .fg-fund-success-screen h2 {
                font-size: 2rem;
                font-weight: 900;
                color: var(--text-color);
                margin: 0 0 8px;
            }

            .fg-fund-success-screen__sub {
                color: var(--text-secondary);
                font-size: 1.05rem;
                line-height: 1.5;
                margin: 0 0 32px;
            }

            .fg-fund-success-screen__card, .fg-fund-success-screen__simulated-email {
                border: 1px solid var(--border-color);
                border-radius: 12px;
                padding: 24px;
                margin-bottom: 20px;
                text-align: left;
                font-size: 0.95rem;
            }

            .fg-fund-success-screen__card h3 {
                margin: 0 0 16px;
                font-size: 1.15rem;
                font-weight: 800;
                border-bottom: 1px solid var(--border-color);
                padding-bottom: 10px;
                color: var(--text-color);
            }

            .fg-fund-success-screen__card span {
                color: var(--text-secondary);
            }

            .fg-fund-success-screen__card strong {
                color: var(--text-color);
            }

            .fg-fund-success-screen__actions {
                display: flex;
                gap: 16px;
                margin-top: 32px;
            }

            .fg-fund-success-screen__actions button {
                padding: 14px 20px;
            }

            /* Form components styling */
            .fg-fund-form {
                display: flex;
                flex-direction: column;
                gap: 18px;
            }

            [data-theme="dark"] .fg-fund-wizard__details-notice,
            [data-theme="dark"] .fg-fund-create__notice {
                background: #25121c;
                border-color: #be185d;
                color: #f472b6;
            }

            .fg-fund-create__stepper,
            .fg-fund-create__step,
            .fg-fund-create__step-line,
            .fg-fund-create__actions,
            .fg-fund-create__notice {
                /* inherits from wizard selectors below via shared rules */
            }

            .fg-fund-create__stepper { display: flex; align-items: center; margin-bottom: 40px; }
            .fg-fund-create__step {
                display: flex; align-items: center; gap: 8px;
                font-size: 0.9rem; font-weight: 700; color: var(--text-secondary);
            }
            .fg-fund-create__step span {
                width: 24px; height: 24px; border-radius: 50%;
                background: var(--hover-bg); color: var(--text-secondary);
                display: flex; align-items: center; justify-content: center;
                font-size: 0.8rem; font-weight: 800;
            }
            .fg-fund-create__step.active { color: #E91E8C; }
            .fg-fund-create__step.active span { background: #E91E8C; color: #fff; }
            .fg-fund-create__step-line { flex: 1; height: 2px; background: var(--border-color); margin: 0 16px; }
            .fg-fund-create__step-line.active { background: #E91E8C; }
            .fg-fund-create__notice {
                padding: 16px; background: #fdf2f8; border: 1px solid #fbcfe8;
                border-radius: 10px; color: #be185d; font-size: 0.9rem;
            }

            .fg-fund-confirm {
                max-width: 520px; margin: 0 auto; text-align: center; padding: 24px 0;
            }
            .fg-fund-confirm__icon { font-size: 3rem; margin-bottom: 12px; }
            .fg-fund-confirm__title { font-size: 1.75rem; font-weight: 900; color: var(--text-color); margin: 0 0 8px; }
            .fg-fund-confirm__project { font-size: 1.1rem; color: var(--text-secondary); margin-bottom: 24px; }
            .fg-fund-confirm__actions { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; }
            .fg-fund-confirm__share span { display: block; font-weight: 700; margin-bottom: 10px; color: var(--text-secondary); }
            .fg-fund-confirm__share-buttons { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

            .fg-fund-donate__tier-match {
                padding: 16px; background: var(--hover-bg);
                border: 1px solid var(--border-color); border-radius: 10px;
                font-size: 0.95rem; line-height: 1.5; color: var(--text-color);
                margin-bottom: 16px;
            }

            .fg-fund-donate--success { max-width: 520px; margin: 0 auto; text-align: center; padding: 32px 0; }
            .fg-fund-donate__success-icon {
                width: 64px; height: 64px; border-radius: 50%;
                background: #E91E8C; color: #fff; font-size: 2rem;
                display: flex; align-items: center; justify-content: center;
                margin: 0 auto 20px; font-weight: 900;
            }
            .fg-fund-donate--success h2 { font-size: 1.5rem; color: var(--text-color); margin-bottom: 12px; }
            .fg-fund-donate__success-message { font-style: italic; color: var(--text-secondary); }
            .fg-fund-donate__success-actions { display: flex; gap: 12px; margin-top: 28px; flex-wrap: wrap; justify-content: center; }

            /* Admin dashboard */
            .fg-fund-admin__header { margin-bottom: 28px; }
            .fg-fund-admin__header h2 {
                font-size: 1.75rem; font-weight: 900; color: var(--text-color); margin: 0 0 6px;
            }
            .fg-fund-admin__header p { color: var(--text-secondary); margin: 0; }

            .fg-fund-admin__stats {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                gap: 16px;
                margin-bottom: 32px;
            }

            .fg-fund-admin__stat-card {
                background: #1a1a2e;
                border: 1px solid var(--border-color);
                border-radius: 12px;
                padding: 20px;
            }

            .fg-fund-admin__stat-value {
                display: block;
                font-size: 1.5rem;
                font-weight: 900;
                color: #E91E8C;
                line-height: 1.1;
            }

            .fg-fund-admin__stat-label {
                display: block;
                margin-top: 6px;
                font-size: 0.8rem;
                color: var(--text-secondary);
                font-weight: 600;
            }

            [data-theme="dark"] .fg-fund-admin__stat-card {
                background: #12121f;
            }

            .fg-fund-admin__section {
                margin-bottom: 32px;
            }

            .fg-fund-admin__section h3 {
                font-size: 1.2rem;
                font-weight: 800;
                color: var(--text-color);
                margin: 0 0 16px;
            }

            .fg-fund-admin__table-wrap {
                overflow-x: auto;
                border: 1px solid var(--border-color);
                border-radius: 12px;
            }

            .fg-fund-admin__table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.9rem;
            }

            .fg-fund-admin__table th,
            .fg-fund-admin__table td {
                padding: 12px 14px;
                text-align: left;
                border-bottom: 1px solid var(--border-color);
                color: var(--text-color);
            }

            .fg-fund-admin__table th {
                background: var(--hover-bg);
                font-weight: 800;
                font-size: 0.75rem;
                text-transform: uppercase;
            }

            .fg-fund-admin__table tbody tr:nth-child(even) {
                background: rgba(0,0,0,0.02);
            }

            [data-theme="dark"] .fg-fund-admin__table tbody tr:nth-child(even) {
                background: rgba(255,255,255,0.03);
            }

            .fg-fund-admin__badge {
                display: inline-block;
                padding: 4px 10px;
                border-radius: 99px;
                font-size: 0.75rem;
                font-weight: 800;
            }

            .fg-fund-admin__badge--active { background: #d1fae5; color: #065f46; }
            .fg-fund-admin__badge--funded { background: #fce7f3; color: #9d174d; }
            .fg-fund-admin__badge--ended { background: #e5e7eb; color: #374151; }
            .fg-fund-admin__badge--ending { background: #ffedd5; color: #9a3412; }

            .fg-fund-admin__actions {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .fg-fund-admin__btn {
                padding: 4px 10px;
                font-family: inherit;
                font-size: 0.75rem;
                font-weight: 700;
                border-radius: 6px;
                border: 1px solid var(--border-color);
                background: var(--hover-bg);
                color: var(--text-color);
                cursor: pointer;
            }

            .fg-fund-admin__btn--danger {
                border-color: #fecaca;
                color: #b91c1c;
            }

            .fg-fund-admin__donations-feed {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .fg-fund-admin__donation-item {
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                border-radius: 10px;
                padding: 16px;
            }

            .fg-fund-admin__donation-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 6px;
            }

            .fg-fund-admin__donation-project {
                margin: 0 0 4px;
                color: var(--text-secondary);
                font-size: 0.9rem;
            }

            .fg-fund-admin__donation-message {
                margin: 0 0 6px;
                font-style: italic;
                color: var(--text-color);
            }

            .fg-fund-admin__donation-date {
                font-size: 0.75rem;
                color: var(--text-secondary);
            }

            .fg-fund-admin__actions-bar {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
            }

            .fg-fund-admin__health-note {
                color: var(--text-secondary);
                font-size: 0.9rem;
            }

            .fg-fund-admin__link {
                color: #E91E8C;
                font-weight: 700;
            }

            /* Responsive */
            @media (max-width: 1024px) {
                .fg-fund-grid {
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;
                }
            }

            @media (max-width: 768px) {
                .fg-fund-grid, .fg-fund-detail__layout, .fg-fund-donate__layout {
                    grid-template-columns: 1fr;
                    gap: 20px;
                }
                .fg-fund-hero {
                    padding: 30px;
                }
                .fg-fund-hero h1 {
                    font-size: 1.85rem;
                }
                .fg-fund-detail__hero-image {
                    height: 250px;
                }
            }

            .fg-fund-lang-bar {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 8px;
                margin-bottom: 12px;
            }
            .fg-fund-lang-bar__select {
                min-height: 44px;
                padding: 8px 12px;
                border-radius: 10px;
                border: 1px solid var(--border-color);
                background: var(--card-bg);
                color: var(--text-color);
            }
            .fg-fund-info {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 44px;
                height: 44px;
                min-width: 44px;
                border: none;
                background: transparent;
                color: #E91E8C;
                cursor: pointer;
                vertical-align: middle;
                padding: 0;
            }
            .fg-fund-info-popover {
                position: absolute;
                z-index: 50;
                max-width: 280px;
                padding: 12px 14px;
                background: var(--card-bg);
                border: 1px solid var(--border-color);
                border-radius: 12px;
                box-shadow: 0 12px 32px rgba(0,0,0,0.12);
                font-size: 0.88rem;
                line-height: 1.45;
                color: var(--text-color);
            }
            .fg-fund-label-row {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                flex-wrap: wrap;
            }
            .fg-fund-toast {
                position: fixed;
                bottom: 24px;
                left: 50%;
                transform: translateX(-50%) translateY(80px);
                background: #1a1a1a;
                color: #fff;
                padding: 14px 22px;
                border-radius: 99px;
                font-weight: 700;
                z-index: 9999;
                opacity: 0;
                transition: transform 0.3s, opacity 0.3s;
                pointer-events: none;
            }
            .fg-fund-toast.visible {
                transform: translateX(-50%) translateY(0);
                opacity: 1;
            }
            .fg-fund-toast--success { background: linear-gradient(135deg, #E91E8C, #ff52ad); }
            .fg-fund-search-row {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                position: relative;
            }
            .fg-fund-search { flex: 1; min-height: 44px; }
            .fg-fund-btn--ghost {
                background: rgba(255,255,255,0.2);
                color: #fff;
                border: 1px solid rgba(255,255,255,0.5);
                margin-left: 8px;
            }
            .fg-fund-gallery {
                overflow: hidden;
                border-radius: 16px;
                margin-bottom: 16px;
                position: relative;
            }
            .fg-fund-gallery__track {
                display: flex;
                transition: transform 0.35s ease;
            }
            .fg-fund-gallery__slide {
                min-width: 100%;
                height: 220px;
                background-size: cover;
                background-position: center;
            }
            .fg-fund-gallery__dots {
                display: flex;
                justify-content: center;
                gap: 8px;
                padding: 10px;
            }
            .fg-fund-gallery__dots span {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: rgba(233,30,140,0.3);
                cursor: pointer;
            }
            .fg-fund-gallery__dots span.active { background: #E91E8C; }
            .fg-fund-preview-banner {
                background: #fff3cd;
                color: #856404;
                padding: 12px 16px;
                border-radius: 10px;
                margin-bottom: 12px;
                font-weight: 700;
            }
            .fg-fund-countdown {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px 16px;
                background: var(--hover-bg);
                border-radius: 12px;
                margin-bottom: 20px;
            }
            .fg-fund-countdown--ended { color: #c62828; font-weight: 800; }
            .fg-fund-faq-item {
                border: 1px solid var(--border-color);
                border-radius: 10px;
                margin-bottom: 8px;
                padding: 0 14px;
            }
            .fg-fund-faq-item summary {
                cursor: pointer;
                font-weight: 700;
                padding: 14px 0;
                min-height: 44px;
            }
            .fg-fund-fee-viz {
                background: var(--hover-bg);
                border-radius: 12px;
                padding: 16px;
                margin-bottom: 20px;
            }
            .fg-fund-fee-viz__list { list-style: none; padding: 0; margin: 8px 0; }
            .fg-fund-fee-viz__list li {
                display: flex;
                justify-content: space-between;
                padding: 6px 0;
                border-bottom: 1px dashed var(--border-color);
            }
            .fg-fund-fee-viz__net { font-size: 1.1rem; color: #E91E8C; font-weight: 800; }
            .fg-fund-draft-row {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
                padding: 16px;
                border: 1px solid var(--border-color);
                border-radius: 12px;
                margin-bottom: 10px;
                background: var(--card-bg);
            }
            .fg-fund-saved-stamp {
                font-size: 0.8rem;
                color: var(--text-secondary);
                font-weight: 600;
            }
            .fg-fund-upload-fallback {
                display: block;
                margin-top: 8px;
                padding: 14px;
                text-align: center;
                border: 2px dashed #E91E8C;
                border-radius: 12px;
                cursor: pointer;
                min-height: 44px;
                font-weight: 700;
                color: #E91E8C;
            }
            .fg-fund-check-row {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-top: 12px;
                min-height: 44px;
            }
            .fg-fund-check-row input { width: 20px; height: 20px; }
            #app-content { position: relative; }
            .fg-fund-connect {
                border: 1px solid var(--border-color);
                border-radius: 14px;
                padding: 18px;
                margin-bottom: 20px;
                background: var(--card-bg);
            }
            .fg-fund-connect--ok { border-color: #38ef7d; }
            .fg-fund-connect--pending { border-color: #ffc107; }
            .fg-fund-connect--warn { background: #fff8e1; }
            .fg-fund-connect__head {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
                flex-wrap: wrap;
            }
            .fg-fund-connect__badge {
                font-size: 0.75rem;
                font-weight: 800;
                padding: 4px 10px;
                border-radius: 99px;
                background: var(--hover-bg);
            }
            .fg-fund-connect--ok .fg-fund-connect__badge {
                background: #e8f5e9;
                color: #2e7d32;
            }
            .fg-fund-verified-badge {
                display: inline-block;
                font-size: 0.7rem;
                font-weight: 800;
                color: #2e7d32;
                background: #e8f5e9;
                padding: 3px 8px;
                border-radius: 6px;
                margin-left: 6px;
                vertical-align: middle;
            }
            .fg-fund-kyc-badge {
                display: inline-block;
                font-size: 0.7rem;
                font-weight: 800;
                color: #1565c0;
                background: #e3f2fd;
                padding: 3px 8px;
                border-radius: 6px;
                margin-left: 6px;
                vertical-align: middle;
            }
            .fg-fund-comments-subhead {
                margin: 20px 0 10px;
                font-size: 0.95rem;
                color: var(--text-secondary);
            }
            .fg-fund-community-comment {
                padding: 12px 14px;
                border: 1px solid var(--border-color);
                border-radius: 12px;
                margin-bottom: 10px;
                background: var(--card-bg);
            }
            .fg-fund-community-comment--reply {
                margin-left: 20px;
                border-left: 3px solid #E91E8C;
            }
            .fg-fund-community-comment__head {
                display: flex;
                justify-content: space-between;
                gap: 8px;
                font-size: 0.85rem;
                margin-bottom: 6px;
            }
            .fg-fund-community-comment__actions {
                display: flex;
                gap: 8px;
                margin-top: 8px;
            }
            .fg-fund-poll {
                margin-top: 16px;
                padding: 14px;
                border-radius: 12px;
                background: var(--hover-bg);
            }
            .fg-fund-poll__q { font-weight: 700; margin-bottom: 10px; }
            .fg-fund-poll__opts { display: flex; flex-direction: column; gap: 8px; }
            .fg-fund-poll__opt {
                text-align: left;
                padding: 10px 12px;
                border-radius: 10px;
                border: 1px solid var(--border-color);
                background: var(--card-bg);
                cursor: pointer;
            }
            .fg-fund-poll__result {
                display: grid;
                grid-template-columns: 1fr auto;
                gap: 6px;
                align-items: center;
                margin-bottom: 8px;
            }
            .fg-fund-poll__bar {
                grid-column: 1 / -1;
                height: 8px;
                background: var(--border-color);
                border-radius: 4px;
                overflow: hidden;
            }
            .fg-fund-poll__bar div {
                height: 100%;
                background: #E91E8C;
            }
            .fg-fund-poll-fields.hidden { display: none; }
            .fg-fund-tier-delivery {
                display: block;
                font-size: 0.8rem;
                color: var(--text-secondary);
                margin: 6px 0;
            }
            .fg-fund-tier-delivery--done { color: #2e7d32; font-weight: 700; }
            .fg-fund-tier-fulfill-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
                padding: 12px 0;
                border-bottom: 1px solid var(--border-color);
            }
            .fg-fund-tier-fulfill-row__meta {
                font-size: 0.8rem;
                color: var(--text-secondary);
            }
            .fg-fund-notify-comment { margin-top: 12px; }
            .fg-fund-publish-hint {
                color: #c62828;
                font-weight: 700;
                font-size: 0.9rem;
            }
            .fg-fund-toast--error { background: #c62828; }
            .fg-fund-rich__toolbar {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
                margin-bottom: 8px;
            }
            .fg-fund-rich__btn {
                min-width: 44px;
                min-height: 44px;
                border: 1px solid var(--border-color);
                border-radius: 10px;
                background: var(--card-bg);
                font-weight: 800;
                cursor: pointer;
            }
            .fg-fund-rich__area {
                min-height: 200px;
                padding: 14px;
                border: 2px solid var(--border-color);
                border-radius: 12px;
                background: var(--card-bg);
                line-height: 1.6;
            }
            .fg-fund-rich-body img { max-width: 100%; border-radius: 8px; }
            .fg-fund-coming__hero {
                height: 240px;
                background: linear-gradient(135deg, #E91E8C, #ff52ad);
                background-size: cover;
                background-position: center;
                border-radius: 16px;
            }
            .fg-fund-coming__card {
                margin-top: -40px;
                padding: 28px;
                background: var(--card-bg);
                border-radius: 16px;
                box-shadow: 0 12px 40px rgba(0,0,0,0.08);
                position: relative;
            }
            .fg-fund-coming__pill {
                background: #E91E8C;
                color: #fff;
                padding: 4px 12px;
                border-radius: 99px;
                font-size: 0.75rem;
                font-weight: 800;
            }
            .fg-fund-analytics__grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                gap: 12px;
                margin: 16px 0;
            }
            .fg-fund-stat-card {
                padding: 16px;
                border-radius: 12px;
                background: var(--hover-bg);
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .fg-fund-stat-card strong { font-size: 1.5rem; color: #E91E8C; }
            .fg-fund-chart__row {
                display: grid;
                grid-template-columns: 100px 1fr 40px;
                gap: 8px;
                align-items: center;
                margin-bottom: 8px;
            }
            .fg-fund-chart__bar-wrap {
                height: 10px;
                background: var(--hover-bg);
                border-radius: 99px;
                overflow: hidden;
            }
            .fg-fund-chart__bar {
                height: 100%;
                background: linear-gradient(90deg, #E91E8C, #ff52ad);
            }
            .fg-fund-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.85rem;
            }
            .fg-fund-table th, .fg-fund-table td {
                padding: 10px;
                border-bottom: 1px solid var(--border-color);
                text-align: left;
            }
            .fg-fund-utm-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px 0;
                border-bottom: 1px dashed var(--border-color);
            }
            .fg-fund-detail-tabs {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                margin: 16px 0;
            }
            .fg-fund-detail-tabs__btn {
                min-height: 44px;
                padding: 10px 18px;
                border-radius: 99px;
                border: 1px solid var(--border-color);
                background: var(--card-bg);
                font-weight: 700;
                cursor: pointer;
            }
            .fg-fund-detail-tabs__btn.active {
                background: #E91E8C;
                color: #fff;
                border-color: #E91E8C;
            }
            .fg-fund-detail__section.hidden { display: none; }
            .fg-fund-update-card {
                padding: 16px;
                border: 1px solid var(--border-color);
                border-radius: 12px;
                margin-bottom: 12px;
            }
            .fg-fund-update-card__date { font-size: 0.8rem; color: var(--text-secondary); }
            .fg-fund-update-card__title { margin: 8px 0; }
            .fg-fund-update-card__img { max-width: 100%; border-radius: 8px; margin-top: 8px; }
            .fg-fund-team-invite {
                display: grid;
                gap: 10px;
                grid-template-columns: 1fr auto auto;
            }
            @media (max-width: 640px) {
                .fg-fund-team-invite { grid-template-columns: 1fr; }
            }
            .fg-fund-team-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px 0;
                border-bottom: 1px solid var(--border-color);
            }
            .fg-fund-review-link-hint {
                font-size: 0.85rem;
                color: var(--text-secondary);
                margin-top: 12px;
            }
            .fg-fund-review-link {
                display: block;
                word-break: break-all;
                margin: 8px 0;
                font-size: 0.75rem;
            }
            .fg-fund-create__toolbar {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                margin-top: 12px;
            }
            .fg-fund-secret-tier-banner {
                background: #e3f2fd;
                color: #1565c0;
                padding: 10px 14px;
                border-radius: 10px;
                font-weight: 700;
                font-size: 0.85rem;
                margin-bottom: 12px;
            }
            .fg-fund-secret-link code {
                display: block;
                font-size: 0.7rem;
                word-break: break-all;
                margin: 6px 0;
            }
            .fg-fund-poll-dashboard-card {
                padding: 14px;
                border: 1px solid var(--border-color);
                border-radius: 12px;
                margin-bottom: 12px;
            }
            .fg-fund-poll-dashboard-card__total {
                font-size: 0.8rem;
                color: var(--text-secondary);
            }
            .fg-fund-analytics__grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                gap: 12px;
                margin-bottom: 20px;
            }
            .fg-fund-btn,
            .fg-fund-filter-chip,
            .fg-fund-amount-btn,
            .fg-fund-share-btn {
                min-height: 44px;
            }
            .fg-fund-btn:focus-visible,
            .fg-fund-filter-chip:focus-visible,
            .fg-fund-detail-tabs__btn:focus-visible,
            .fg-fund-poll__opt:focus-visible {
                outline: 2px solid #E91E8C;
                outline-offset: 2px;
            }
            @media (max-width: 640px) {
                .fg-fund-grid { grid-template-columns: 1fr; }
                .fg-fund-detail__layout { flex-direction: column; }
                .fg-fund-detail__sidebar { width: 100%; position: static; }
                .fg-fund-donate__layout { flex-direction: column; }
                .fg-fund-donate__sidebar { width: 100%; }
                .fg-fund-create__stepper { flex-wrap: wrap; gap: 8px; }
                .fg-fund-create__step-line { display: none; }
                .fg-fund-search-row .fg-input { width: 100%; }
                .fg-fund-filters { overflow-x: auto; flex-wrap: nowrap; padding-bottom: 8px; }
                .fg-fund-table { display: block; overflow-x: auto; }
                .fg-fund-tier-fulfill-row { flex-direction: column; align-items: flex-start; }
                .fg-fund-wizard__add-tier-form .fg-input,
                .fg-fund-wizard__add-tier-form .fg-textarea,
                .fg-fund-wizard__add-tier-form .fg-select { width: 100%; }
            }
        `;

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }
}